import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase/config';
import {
  Report,
  ReportType,
  ReportFilters,
  SalesReportData,
  InventoryReportData,
  PurchaseOrderReportData,
  LowStockReportData,
  LowStockItem,
  WarehousePerformanceData,
  ProductPerformanceData,
  RevenueReportData,
  RevenueDayItem,
  ReturnsCancellationsReportData,
  ReturnCancelOrderItem,
  ProfitLossReportData,
  ProfitLossProductItem,
  ProfitLossCostItem,
  CostReportData,
  CostCategoryItem,
  CostSupplierItem,
  CostTimelineItem,
  PaymentReportData,
  PaymentMethodItem,
  SupplierArrearItem,
  CustomerDueItem,
  PaymentTimelineItem,
  ForecastReportData,
  ForecastPrediction,
  AIInsights,
} from './Report';
import { WarehouseInventoryService } from '../product/WarehouseInventoryService';
import { WarehouseService } from '../warehouse/warehouseService';
import {
  CostType,
  CostStatus,
  RecurrenceFrequency,
  COST_CATEGORY_LABELS,
  OCCURRENCES_PER_YEAR,
} from '../cost/cost';

const REPORTS_COLLECTION         = 'reports';
const ORDERS_COLLECTION          = 'orders';
const PRODUCTS_COLLECTION        = 'products';
const PURCHASE_ORDERS_COLLECTION = 'purchaseOrders';
const WAREHOUSES_COLLECTION      = 'warehouses';
const CATEGORIES_COLLECTION      = 'categories';
const COSTS_COLLECTION           = 'costs';

const DELIVERED_STATUSES     = ['delivered', 'Delivered'];
const RETURN_CANCEL_STATUSES = ['returned', 'Returned', 'cancelled', 'Cancelled', 'refunded', 'Refunded'];

export interface PaginatedReportsResult {
  reports: Report[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface ReportStats {
  total: number;
  completed: number;
  generating: number;
  failed: number;
  byType: { [k: string]: number };
}

export class ReportService {

  private static formatPaymentMethod(method: string): string {
    const map: { [k: string]: string } = {
      bank_transfer: 'Bank Transfer', credit_card: 'Credit Card', debit_card: 'Debit Card',
      cash_on_delivery: 'Cash on Delivery', paypal: 'PayPal', stripe: 'Stripe', cod: 'Cash on Delivery',
    };
    return map[method] || method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  private static formatStatus(status: string): string {
    const map: { [k: string]: string } = {
      pending: 'Pending', processing: 'Processing', confirmed: 'Confirmed', shipped: 'Shipped',
      delivered: 'Delivered', cancelled: 'Cancelled', returned: 'Returned', refunded: 'Refunded',
      draft: 'Draft', approved: 'Approved', received: 'Received', paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid',
    };
    return map[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  private static isDelivered(status: string): boolean {
    return DELIVERED_STATUSES.includes(status);
  }

  private static isReturnOrCancel(status: string): boolean {
    return RETURN_CANCEL_STATUSES.includes(status);
  }

  /* ===================== GET REPORTS PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * Also fixed the broken count query — the original used
   * `queryConstraints.filter(c => c.type === 'where')` which always
   * returned an empty array (Firestore constraint objects have no `.type`
   * property), so getCountFromServer was always returning the unfiltered total.
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ================================================================= */
  static async getReportsPaginated(
    pageSize = 10,
    page = 1,
    searchQuery?: string,
    typeFilter?: ReportType,
    statusFilter?: 'completed' | 'generating' | 'failed'
  ): Promise<PaginatedReportsResult> {
    try {
      const reports: Report[] = [];
      const collectionRef = collection(db, REPORTS_COLLECTION);

      // Build where-constraints (shared between count + seek + page queries)
      const whereConstraints: any[] = [];
      if (typeFilter)   whereConstraints.push(where('reportType', '==', typeFilter));
      if (statusFilter) whereConstraints.push(where('status',     '==', statusFilter));

      const orderConstraint = orderBy('generatedAt', 'desc');

      // ── Total count (0 doc reads) — FIX: pass whereConstraints directly ──
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...whereConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...whereConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...whereConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...whereConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const report: Report = {
          id: docSnap.id, reportType: data.reportType, reportName: data.reportName,
          description: data.description || '', filters: this.deserializeFilters(data.filters || {}),
          status: data.status, generatedAt: data.generatedAt?.toDate() || new Date(),
          generatedBy: data.generatedBy, generatedByName: data.generatedByName,
          data: this.deserializeReportData(data.data || {}), summary: data.summary || { totalRecords: 0 }, error: data.error,
          aiInsights: data.aiInsights ? this.deserializeReportData(data.aiInsights) : undefined,
        };
        if (searchQuery && searchQuery.trim() !== '') {
          const lower = searchQuery.toLowerCase();
          const matches =
            report.reportName.toLowerCase().includes(lower) ||
            (report.description?.toLowerCase().includes(lower)) ||
            (report.generatedByName?.toLowerCase().includes(lower));
          if (matches) reports.push(report);
        } else {
          reports.push(report);
        }
      });

      return {
        reports,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated reports:', error);
      throw error;
    }
  }

  static async generateReport(
    reportType: ReportType, reportName: string, description: string,
    filters: ReportFilters, userId: string, userName: string
  ): Promise<string> {
    try {
      const reportRef = await addDoc(collection(db, REPORTS_COLLECTION), {
        reportType, reportName, description, filters: this.serializeFilters(filters),
        status: 'generating', generatedAt: serverTimestamp(),
        generatedBy: userId, generatedByName: userName, data: {}, summary: { totalRecords: 0 },
      });
      const reportId = reportRef.id;

      try {
        let data: any, summary: any, aiInsights: AIInsights | undefined;
        switch (reportType) {
          case 'sales':                 { const d = await this.generateSalesReport(filters);                data = d; summary = d.summary; break; }
          case 'inventory':             { const d = await this.generateInventoryReport(filters);            data = d; summary = d.summary; break; }
          case 'purchase_orders':       { const d = await this.generatePurchaseOrderReport(filters);        data = d; summary = d.summary; break; }
          case 'low_stock':             { const d = await this.generateLowStockReport(filters);             data = d; summary = d.summary; break; }
          case 'warehouse_performance': { const d = await this.generateWarehousePerformanceReport(filters); data = d; summary = d.summary; break; }
          case 'product_performance':   { const d = await this.generateProductPerformanceReport(filters);   data = d; summary = d.summary; break; }
          case 'revenue':               { const d = await this.generateRevenueReport(filters);              data = d; summary = d.summary; break; }
          case 'returns_cancellations': { const d = await this.generateReturnsCancellationsReport(filters); data = d; summary = d.summary; break; }
          case 'profit_loss':           { const d = await this.generateProfitLossReport(filters);           data = d; summary = d.summary; break; }
          case 'cost':                  { const d = await this.generateCostReport(filters);                 data = d; summary = d.summary; break; }
          case 'payments':              { const d = await this.generatePaymentsReport(filters);             data = d; summary = d.summary; break; }
          case 'forecast':              { const d = await this.generateForecastReport(filters);             data = d.report; summary = d.report.summary; aiInsights = d.aiInsights; break; }
          default: throw new Error('Invalid report type');
        }

        await updateDoc(reportRef, {
          status: 'completed', data: this.serializeReportData(data),
          summary: {
            ...summary,
            totalRecords:
              data.purchaseOrders?.length || data.orders?.length || data.products?.length ||
              data.items?.length || data.warehouses?.length || data.dailyRevenue?.length ||
              data.revenueByProduct?.length || data.costByCategory?.length ||
              data.costBySupplier?.length || data.incomingByMethod?.length ||
              data.supplierArrears?.length || data.predictions?.length || 0,
          },
          ...(aiInsights ? { aiInsights: this.serializeReportData(aiInsights) } : {}),
        });
        return reportId;
      } catch (error) {
        await updateDoc(reportRef, { status: 'failed', error: error instanceof Error ? error.message : 'Unknown error' });
        throw error;
      }
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  }

  private static serializeFilters(filters: ReportFilters): any {
    return {
      dateFrom: filters.dateFrom ? Timestamp.fromDate(filters.dateFrom) : null,
      dateTo:   filters.dateTo   ? Timestamp.fromDate(filters.dateTo)   : null,
      warehouseId: filters.warehouseId || null, categoryId: filters.categoryId || null,
      subcategoryId: filters.subcategoryId || null, productId: filters.productId || null,
      status: filters.status || null, supplierId: filters.supplierId || null,
    };
  }

  private static deserializeFilters(filters: any): ReportFilters {
    return {
      dateFrom: filters.dateFrom?.toDate(), dateTo: filters.dateTo?.toDate(),
      warehouseId: filters.warehouseId, categoryId: filters.categoryId,
      subcategoryId: filters.subcategoryId, productId: filters.productId,
      status: filters.status, supplierId: filters.supplierId,
    };
  }

  private static serializeReportData(data: any): any {
    if (!data) return data;
    if (Array.isArray(data)) return data.map(i => this.serializeReportData(i));
    if (typeof data === 'object' && data !== null) {
      if (data instanceof Date) return Timestamp.fromDate(data);
      const out: any = {};
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          const v = this.serializeReportData(data[key]);
          if (v !== undefined) out[key] = v;
        }
      }
      return out;
    }
    return data;
  }

  private static deserializeReportData(data: any): any {
    if (!data) return data;
    if (Array.isArray(data)) return data.map(i => this.deserializeReportData(i));
    if (typeof data === 'object' && data !== null) {
      if (data.toDate && typeof data.toDate === 'function') return data.toDate();
      const out: any = {};
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) out[key] = this.deserializeReportData(data[key]);
      }
      return out;
    }
    return data;
  }

  private static async getCategoryName(categoryId: string): Promise<string> {
    try {
      const snap = await getDoc(doc(db, CATEGORIES_COLLECTION, categoryId));
      return snap.exists() ? snap.data().name || 'Unknown' : 'Unknown';
    } catch { return 'Unknown'; }
  }

  private static async generateSalesReport(filters: ReportFilters): Promise<SalesReportData> {
    const ordersRef = collection(db, ORDERS_COLLECTION);
    let constraints: any[] = [];
    if (filters.dateFrom) constraints.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
    if (filters.dateTo)   constraints.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
    if (filters.status)   constraints.push(where('status', '==', filters.status));
    constraints.push(orderBy('orderDate', 'desc'));

    const snapshot = await getDocs(query(ordersRef, ...constraints));

    const orders = snapshot.docs.map(d => {
      const data = d.data();
      return {
        orderId: d.id,
        orderNumber:    data.orderNumber || 'N/A',
        orderDate:      data.orderDate?.toDate() || new Date(),
        customerName:   data.customerName || 'Unknown',
        customerEmail:  data.customerEmail || 'N/A',
        items:          data.items?.length || 0,
        subtotal:       data.subtotal || 0,
        taxAmount:      data.taxAmount || 0,
        shippingCost:   data.shippingCost || 0,
        discountAmount: data.discountAmount || 0,
        totalAmount:    data.totalAmount || 0,
        paymentStatus:  data.paymentStatus || 'pending',
        paymentMethod:  this.formatPaymentMethod(data.paymentMethod || 'N/A'),
        status:         this.formatStatus(data.status || 'pending'),
      };
    });

    const totalRevenue  = orders.reduce((s, o) => s + o.totalAmount, 0);
    const totalTax      = orders.reduce((s, o) => s + o.taxAmount, 0);
    const totalShipping = orders.reduce((s, o) => s + o.shippingCost, 0);
    const totalDiscount = orders.reduce((s, o) => s + o.discountAmount, 0);

    const productMap = new Map<string, { quantity: number; revenue: number }>();
    for (const d of snapshot.docs) {
      for (const item of (d.data().items || [])) {
        const name = item.productName || 'Unknown Product';
        const ex   = productMap.get(name) || { quantity: 0, revenue: 0 };
        productMap.set(name, { quantity: ex.quantity + (item.quantity || 0), revenue: ex.revenue + (item.totalPrice || 0) });
      }
    }
    const topProducts = Array.from(productMap.entries())
      .map(([productName, d]) => ({ productName, ...d }))
      .sort((a, b) => b.revenue - a.revenue).slice(0, 10);

    const statusBreakdown: { [k: string]: number } = {};
    orders.forEach(o => { statusBreakdown[o.status] = (statusBreakdown[o.status] || 0) + 1; });

    const paymentMethodBreakdown: { [k: string]: number } = {};
    orders.forEach(o => { paymentMethodBreakdown[o.paymentMethod] = (paymentMethodBreakdown[o.paymentMethod] || 0) + 1; });

    return {
      orders,
      summary: {
        totalOrders: orders.length, totalRevenue, totalTax, totalShipping, totalDiscount,
        averageOrderValue: orders.length > 0 ? totalRevenue / orders.length : 0,
        topProducts, statusBreakdown, paymentMethodBreakdown,
      },
    };
  }

  private static async generateInventoryReport(filters: ReportFilters): Promise<InventoryReportData> {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    let constraints: any[] = [];
    if (filters.categoryId)    constraints.push(where('categoryId',    '==', filters.categoryId));
    if (filters.subcategoryId) constraints.push(where('subcategoryId', '==', filters.subcategoryId));
    const snapshot = await getDocs(constraints.length > 0 ? query(productsRef, ...constraints) : query(productsRef));

    const warehouses = await WarehouseService.getActiveWarehouses();
    const products: any[] = [];
    let totalVariants = 0, totalStock = 0, totalValue = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const productId = docSnap.id;
      const categoryName    = data.categoryId    ? await this.getCategoryName(data.categoryId)    : 'Uncategorized';
      const subcategoryName = data.subcategoryId ? await this.getCategoryName(data.subcategoryId) : '';

      let productTotalStock = 0, productStockValue = 0;
      const warehousesList: any[] = [], variants: any[] = [];

      if (data.hasVariants && data.variants) {
        totalVariants += data.variants.length;
        for (const variant of data.variants) {
          let variantTotalStock = 0;
          const effectivePrice = (variant.price || 0) * (1 - (variant.discount || 0) / 100);
          for (const warehouse of warehouses) {
            const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId, variant.id);
            if (inv) {
              variantTotalStock += inv.quantity;
              const ex = warehousesList.find(w => w.warehouseId === warehouse.id);
              if (ex) ex.quantity += inv.quantity;
              else warehousesList.push({ warehouseId: warehouse.id!, warehouseName: warehouse.name, quantity: inv.quantity, status: inv.isOutOfStock ? 'out_of_stock' : inv.isLowStock ? 'low_stock' : 'in_stock' });
            }
          }
          variants.push({ variantId: variant.id, variantName: variant.variantSelections?.map((s: any) => s.variantValue).join(' - ') || 'Unknown', sku: variant.sku, stock: variantTotalStock, price: effectivePrice });
          productTotalStock += variantTotalStock;
          productStockValue += effectivePrice * variantTotalStock;
        }
      } else {
        const effectivePrice = (data.price || 0) * (1 - (data.discount || 0) / 100);
        for (const warehouse of warehouses) {
          const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId);
          if (inv) {
            productTotalStock += inv.quantity;
            warehousesList.push({ warehouseId: warehouse.id!, warehouseName: warehouse.name, quantity: inv.quantity, status: inv.isOutOfStock ? 'out_of_stock' : inv.isLowStock ? 'low_stock' : 'in_stock' });
          }
        }
        productStockValue = effectivePrice * productTotalStock;
      }

      const isOutOfStock = productTotalStock === 0;
      const isLowStock   = !isOutOfStock && productTotalStock <= (data.minStockLevel || 0);
      const status       = isOutOfStock ? 'out_of_stock' : isLowStock ? 'low_stock' : 'in_stock';
      const productItem: any = { productId, productName: data.name || 'Unknown', sku: data.sku || 'N/A', category: categoryName, subcategory: subcategoryName || '', hasVariants: data.hasVariants || false, totalStock: productTotalStock, stockValue: productStockValue, warehouses: warehousesList, status };
      if (variants.length > 0) productItem.variants = variants;
      products.push(productItem);
      totalStock += productTotalStock;
      totalValue += productStockValue;
    }

    return {
      products,
      summary: { totalProducts: products.length, totalVariants, totalStock, totalValue, lowStockItems: products.filter(p => p.status === 'low_stock').length, outOfStockItems: products.filter(p => p.status === 'out_of_stock').length, inStockItems: products.filter(p => p.status === 'in_stock').length },
    };
  }

  private static async generatePurchaseOrderReport(filters: ReportFilters): Promise<PurchaseOrderReportData> {
    const poRef = collection(db, PURCHASE_ORDERS_COLLECTION);
    let constraints: any[] = [];
    if (filters.dateFrom)   constraints.push(where('createdAt',   '>=', Timestamp.fromDate(filters.dateFrom)));
    if (filters.dateTo)     constraints.push(where('createdAt',   '<=', Timestamp.fromDate(filters.dateTo)));
    if (filters.status)     constraints.push(where('status',       '==', filters.status));
    if (filters.supplierId) constraints.push(where('supplierId',   '==', filters.supplierId));
    constraints.push(orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(query(poRef, ...constraints));
    const purchaseOrders = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        poId: docSnap.id, poNumber: data.poNumber || 'N/A',
        supplierName: data.supplierName || 'Unknown Supplier', supplierEmail: data.supplierEmail || 'N/A', supplierPhone: data.supplierPhone || 'N/A',
        orderDate: data.createdAt?.toDate() || new Date(), expectedDelivery: data.expectedDeliveryDate?.toDate() || null,
        actualDelivery: data.actualDeliveryDate?.toDate() || data.receivedAt?.toDate() || null,
        approvedAt: data.approvedAt?.toDate() || null, receivedAt: data.receivedAt?.toDate() || null,
        items: data.items?.length || 0, subtotal: data.subtotal || 0, taxAmount: data.taxAmount || 0,
        shippingCost: data.shippingCost || 0, totalAmount: data.totalAmount || 0, paidAmount: data.paidAmount || 0,
        status: this.formatStatus(data.status || 'draft'), paymentStatus: this.formatStatus(data.paymentStatus || 'pending'),
        hasReturns: data.hasReturns || false,
      };
    });

    const totalAmount = purchaseOrders.reduce((s, p) => s + p.totalAmount, 0);
    const totalPaid   = purchaseOrders.reduce((s, p) => s + p.paidAmount,  0);
    const statusBreakdown: { [k: string]: number } = {};
    purchaseOrders.forEach(p => { statusBreakdown[p.status] = (statusBreakdown[p.status] || 0) + 1; });
    const paymentStatusBreakdown: { [k: string]: number } = {};
    purchaseOrders.forEach(p => { paymentStatusBreakdown[p.paymentStatus] = (paymentStatusBreakdown[p.paymentStatus] || 0) + 1; });
    const supplierMap = new Map<string, { totalAmount: number; poCount: number }>();
    purchaseOrders.forEach(p => { const ex = supplierMap.get(p.supplierName) || { totalAmount: 0, poCount: 0 }; supplierMap.set(p.supplierName, { totalAmount: ex.totalAmount + p.totalAmount, poCount: ex.poCount + 1 }); });

    return {
      purchaseOrders,
      summary: { totalPOs: purchaseOrders.length, totalAmount, totalPaid, totalPending: totalAmount - totalPaid, statusBreakdown, paymentStatusBreakdown, topSuppliers: Array.from(supplierMap.entries()).map(([supplierName, d]) => ({ supplierName, ...d })).sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 10) },
    };
  }

  private static async generateLowStockReport(filters: ReportFilters): Promise<LowStockReportData> {
    const items: LowStockItem[] = [];
    const warehouses = await WarehouseService.getActiveWarehouses();
    const snapshot   = await getDocs(collection(db, PRODUCTS_COLLECTION));

    for (const productDoc of snapshot.docs) {
      const productData = productDoc.data();
      const productId   = productDoc.id;
      const productName = productData.name || 'Unknown Product';
      for (const warehouse of warehouses) {
        if (productData.hasVariants && productData.variants) {
          for (const variant of productData.variants) {
            const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId, variant.id);
            if (inv && (inv.isLowStock || inv.isOutOfStock)) {
              const variantName = variant.variantSelections?.map((s: any) => s.variantValue).join(' - ') || 'Unknown';
              const item: any = { productId, productName, sku: inv.sku || variant.sku || 'N/A', warehouseId: warehouse.id!, warehouseName: warehouse.name, currentStock: inv.quantity, minStock: inv.minStockLevel, maxStock: inv.maxStockLevel, deficit: Math.max(0, inv.minStockLevel - inv.quantity), status: inv.isOutOfStock ? 'out_of_stock' : 'low_stock' };
              if (variant.id)  item.variantId   = variant.id;
              if (variantName) item.variantName  = variantName;
              items.push(item);
            }
          }
        } else {
          const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId);
          if (inv && (inv.isLowStock || inv.isOutOfStock)) {
            items.push({ productId, productName, sku: inv.sku || productData.sku || 'N/A', warehouseId: warehouse.id!, warehouseName: warehouse.name, currentStock: inv.quantity, minStock: inv.minStockLevel, maxStock: inv.maxStockLevel, deficit: Math.max(0, inv.minStockLevel - inv.quantity), status: inv.isOutOfStock ? 'out_of_stock' : 'low_stock' });
          }
        }
      }
    }

    return {
      items,
      summary: { totalLowStockItems: items.filter(i => i.status === 'low_stock').length, totalOutOfStockItems: items.filter(i => i.status === 'out_of_stock').length, affectedWarehouses: new Set(items.map(i => i.warehouseId)).size, affectedProducts: new Set(items.map(i => i.productId)).size },
    };
  }

  private static async generateWarehousePerformanceReport(filters: ReportFilters): Promise<WarehousePerformanceData> {
    const snapshot   = await getDocs(collection(db, WAREHOUSES_COLLECTION));
    const warehouses = snapshot.docs.map(docSnap => {
      const data          = docSnap.data();
      const capacityUsed  = data.capacity?.used  || 0;
      const capacityTotal = data.capacity?.total || 1;
      return { warehouseId: docSnap.id, warehouseName: data.name || 'Unknown', code: data.code || 'N/A', address: `${data.address?.street || ''}, ${data.address?.city || ''}, ${data.address?.state || ''}`.trim(), manager: data.contact?.manager || 'N/A', totalProducts: data.stats?.productCount || 0, totalStock: data.stats?.totalStock || 0, stockValue: 0, capacityUsed, capacityTotal, capacityAvailable: data.capacity?.available || 0, utilizationRate: Math.round((capacityUsed / capacityTotal) * 100), lowStockCount: data.stats?.lowStockCount || 0, outOfStockCount: data.stats?.outOfStockCount || 0 };
    });

    const totalCapacity  = warehouses.reduce((s, w) => s + w.capacityTotal,    0);
    const totalUsed      = warehouses.reduce((s, w) => s + w.capacityUsed,     0);
    const totalAvailable = warehouses.reduce((s, w) => s + w.capacityAvailable,0);
    const avgUtil        = warehouses.length > 0 ? warehouses.reduce((s, w) => s + w.utilizationRate, 0) / warehouses.length : 0;
    const mostUtilized   = warehouses.reduce((mx, w) => w.utilizationRate > mx.utilizationRate ? w : mx, warehouses[0] || { warehouseName: 'N/A', utilizationRate: 0 });
    const leastUtilized  = warehouses.reduce((mn, w) => w.utilizationRate < mn.utilizationRate ? w : mn, warehouses[0] || { warehouseName: 'N/A', utilizationRate: 0 });

    return { warehouses, summary: { totalWarehouses: warehouses.length, totalCapacity, totalUsed, totalAvailable, averageUtilization: Math.round(avgUtil), mostUtilizedWarehouse: mostUtilized.warehouseName, leastUtilizedWarehouse: leastUtilized.warehouseName } };
  }

  private static async generateProductPerformanceReport(filters: ReportFilters): Promise<ProductPerformanceData> {
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    let pC: any[] = [];
    if (filters.categoryId)    pC.push(where('categoryId',    '==', filters.categoryId));
    if (filters.subcategoryId) pC.push(where('subcategoryId', '==', filters.subcategoryId));
    const productsSnapshot = await getDocs(pC.length > 0 ? query(productsRef, ...pC) : query(productsRef));

    let oC: any[] = [];
    if (filters.dateFrom) oC.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
    if (filters.dateTo)   oC.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
    const ordersSnapshot = await getDocs(oC.length > 0 ? query(collection(db, ORDERS_COLLECTION), ...oC) : query(collection(db, ORDERS_COLLECTION)));

    const salesMap = new Map<string, { totalSold: number; revenue: number }>();
    for (const orderDoc of ordersSnapshot.docs) {
      if (!this.isDelivered(orderDoc.data().status)) continue;
      for (const item of (orderDoc.data().items || [])) {
        const pid = item.productId;
        const ex  = salesMap.get(pid) || { totalSold: 0, revenue: 0 };
        salesMap.set(pid, { totalSold: ex.totalSold + (item.quantity || 0), revenue: ex.revenue + (item.totalPrice || 0) });
      }
    }

    const warehouses = await WarehouseService.getActiveWarehouses();
    const products: any[] = [];

    for (const docSnap of productsSnapshot.docs) {
      const data      = docSnap.data();
      const productId = docSnap.id;
      const sales     = salesMap.get(productId) || { totalSold: 0, revenue: 0 };
      const categoryName    = data.categoryId    ? await this.getCategoryName(data.categoryId)    : 'Uncategorized';
      const subcategoryName = data.subcategoryId ? await this.getCategoryName(data.subcategoryId) : '';

      let currentStock = 0;
      for (const warehouse of warehouses) {
        if (data.hasVariants && data.variants) {
          for (const variant of data.variants) {
            const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId, variant.id);
            if (inv) currentStock += inv.quantity;
          }
        } else {
          const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId);
          if (inv) currentStock += inv.quantity;
        }
      }

      let averagePrice = 0, totalDiscount = 0;
      if (data.hasVariants && data.variants) {
        const valid = data.variants.filter((v: any) => v.price > 0);
        if (valid.length > 0) { averagePrice = valid.reduce((s: number, v: any) => s + (v.price || 0), 0) / valid.length; totalDiscount = valid.reduce((s: number, v: any) => s + (v.discount || 0), 0) / valid.length; }
      } else { averagePrice = data.price || 0; totalDiscount = data.discount || 0; }
      if (sales.totalSold > 0) averagePrice = sales.revenue / sales.totalSold;

      products.push({ productId, productName: data.name || 'Unknown', sku: data.sku || 'N/A', category: categoryName, subcategory: subcategoryName || '', hasVariants: data.hasVariants || false, totalSold: sales.totalSold, revenue: sales.revenue, averagePrice, currentStock, stockTurnover: currentStock > 0 ? Math.round((sales.totalSold / currentStock) * 100) / 100 : 0, isFeatured: data.isFeatured || false, discount: totalDiscount });
    }

    const totalRevenue   = products.reduce((s, p) => s + p.revenue,       0);
    const totalUnitsSold = products.reduce((s, p) => s + p.totalSold,      0);
    const avgTurnover    = products.length > 0 ? products.reduce((s, p) => s + p.stockTurnover, 0) / products.length : 0;
    const avgDiscount    = products.length > 0 ? products.reduce((s, p) => s + p.discount,      0) / products.length : 0;
    const bestSelling    = products.reduce((mx, p) => p.totalSold > mx.totalSold ? p : mx, products[0] || { productName: 'N/A', totalSold: 0 });
    const worstSelling   = products.reduce((mn, p) => (p.totalSold < mn.totalSold && p.totalSold > 0) ? p : mn, products.find(p => p.totalSold > 0) || { productName: 'N/A', totalSold: 0 });

    return { products, summary: { totalProducts: products.length, totalRevenue, totalUnitsSold, bestSellingProduct: bestSelling.productName, worstSellingProduct: worstSelling.productName, averageStockTurnover: Math.round(avgTurnover * 100) / 100, averageDiscount: Math.round(avgDiscount * 100) / 100 } };
  }

  private static async generateRevenueReport(filters: ReportFilters): Promise<RevenueReportData> {
    const ordersRef = collection(db, ORDERS_COLLECTION);
    let constraints: any[] = [];
    if (filters.dateFrom) constraints.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
    if (filters.dateTo)   constraints.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
    constraints.push(orderBy('orderDate', 'asc'));

    const snapshot = await getDocs(query(ordersRef, ...constraints));
    const dailyRevenueMap    = new Map<string, { totalOrders: number; totalRevenue: number; totalTax: number; totalShipping: number; totalDiscount: number }>();
    const productRevenueMap  = new Map<string, { productId: string; productName: string; totalSold: number; revenue: number }>();
    const categoryRevenueMap = new Map<string, { categoryId: string; revenue: number }>();

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (!this.isDelivered(data.status)) continue;
      const orderDate = data.orderDate?.toDate();
      if (!orderDate) continue;
      const dateKey = orderDate.toISOString().split('T')[0];
      const ex = dailyRevenueMap.get(dateKey) || { totalOrders: 0, totalRevenue: 0, totalTax: 0, totalShipping: 0, totalDiscount: 0 };
      dailyRevenueMap.set(dateKey, { totalOrders: ex.totalOrders + 1, totalRevenue: ex.totalRevenue + (data.totalAmount || 0), totalTax: ex.totalTax + (data.taxAmount || 0), totalShipping: ex.totalShipping + (data.shippingCost || 0), totalDiscount: ex.totalDiscount + (data.discountAmount || 0) });
      for (const item of (data.items || [])) {
        const pid = item.productId, pn = item.productName || 'Unknown Product';
        const ex2 = productRevenueMap.get(pid) || { productId: pid, productName: pn, totalSold: 0, revenue: 0 };
        productRevenueMap.set(pid, { ...ex2, totalSold: ex2.totalSold + (item.quantity || 0), revenue: ex2.revenue + (item.totalPrice || 0) });
      }
    }

    const productsSnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
    productRevenueMap.forEach((pd, pid) => {
      const pDoc = productsSnapshot.docs.find(d => d.id === pid);
      if (pDoc) { const cid = pDoc.data().categoryId; if (cid) { const ex = categoryRevenueMap.get(cid) || { categoryId: cid, revenue: 0 }; categoryRevenueMap.set(cid, { categoryId: cid, revenue: ex.revenue + pd.revenue }); } }
    });

    const dailyRevenue: RevenueDayItem[] = Array.from(dailyRevenueMap.entries())
      .map(([dateStr, d]) => ({ date: new Date(dateStr), totalOrders: d.totalOrders, totalRevenue: d.totalRevenue, totalTax: d.totalTax, totalShipping: d.totalShipping, totalDiscount: d.totalDiscount, netRevenue: d.totalRevenue - d.totalTax - d.totalShipping + d.totalDiscount, averageOrderValue: d.totalOrders > 0 ? d.totalRevenue / d.totalOrders : 0 }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const totalRevSum   = dailyRevenue.reduce((s, d) => s + d.totalRevenue, 0);
    const topProducts   = Array.from(productRevenueMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10).map(p => ({ ...p, percentageOfTotal: totalRevSum > 0 ? (p.revenue / totalRevSum) * 100 : 0 }));
    const topCategories = await Promise.all(Array.from(categoryRevenueMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10).map(async c => ({ categoryId: c.categoryId, categoryName: await this.getCategoryName(c.categoryId), revenue: c.revenue, percentageOfTotal: totalRevSum > 0 ? (c.revenue / totalRevSum) * 100 : 0 })));

    const totalNetRevenue = dailyRevenue.reduce((s, d) => s + d.netRevenue,    0);
    const totalOrders     = dailyRevenue.reduce((s, d) => s + d.totalOrders,   0);
    const totalTax        = dailyRevenue.reduce((s, d) => s + d.totalTax,      0);
    const totalShipping   = dailyRevenue.reduce((s, d) => s + d.totalShipping, 0);
    const totalDiscount   = dailyRevenue.reduce((s, d) => s + d.totalDiscount, 0);
    const highestDay      = dailyRevenue.reduce((mx, d) => d.totalRevenue > mx.totalRevenue ? d : mx, dailyRevenue[0] || { totalRevenue: 0, date: new Date() });
    const lowestDay       = dailyRevenue.reduce((mn, d) => d.totalRevenue < mn.totalRevenue ? d : mn, dailyRevenue[0] || { totalRevenue: 0, date: new Date() });

    let growthRate = 0;
    if (dailyRevenue.length >= 2) {
      const half = Math.floor(dailyRevenue.length / 2);
      const firstHalfRev  = dailyRevenue.slice(0, half).reduce((s, d) => s + d.totalRevenue, 0);
      const secondHalfRev = dailyRevenue.slice(half).reduce((s, d) => s + d.totalRevenue, 0);
      if (firstHalfRev > 0) growthRate = ((secondHalfRev - firstHalfRev) / firstHalfRev) * 100;
    }

    return { dailyRevenue, topProducts, topCategories, summary: { totalRevenue: totalRevSum, totalNetRevenue, totalOrders, totalTax, totalShipping, totalDiscount, averageOrderValue: totalOrders > 0 ? totalRevSum / totalOrders : 0, averageDailyRevenue: dailyRevenue.length > 0 ? totalRevSum / dailyRevenue.length : 0, highestRevenueDay: highestDay.date.toLocaleDateString(), lowestRevenueDay: lowestDay.date.toLocaleDateString(), growthRate: Math.round(growthRate * 100) / 100 } };
  }

  private static async generateReturnsCancellationsReport(filters: ReportFilters): Promise<ReturnsCancellationsReportData> {
    const ordersRef = collection(db, ORDERS_COLLECTION);
    let constraints: any[] = [];
    if (filters.dateFrom) constraints.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
    if (filters.dateTo)   constraints.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
    constraints.push(orderBy('orderDate', 'desc'));

    const snapshot     = await getDocs(query(ordersRef, ...constraints));
    const filteredDocs = snapshot.docs.filter(d => this.isReturnOrCancel(d.data().status));

    const orders: ReturnCancelOrderItem[] = filteredDocs.map(d => {
      const data = d.data();
      return { orderId: d.id, orderNumber: data.orderNumber || 'N/A', orderDate: data.orderDate?.toDate() || new Date(), customerName: data.customerName || 'Unknown', customerEmail: data.customerEmail || 'N/A', totalAmount: data.totalAmount || 0, status: this.formatStatus(data.status) as 'Returned' | 'Cancelled' | 'Refunded', paymentMethod: this.formatPaymentMethod(data.paymentMethod || 'N/A'), paymentStatus: this.formatStatus(data.paymentStatus || 'N/A'), items: data.items?.length || 0, reason: data.cancellationReason || data.returnReason || '' };
    });

    const productMap = new Map<string, { quantity: number; revenue: number }>();
    for (const d of filteredDocs) {
      const data = d.data();
      if (!['returned', 'Returned', 'refunded', 'Refunded'].includes(data.status)) continue;
      for (const item of (data.items || [])) {
        const name = item.productName || 'Unknown Product';
        const ex   = productMap.get(name) || { quantity: 0, revenue: 0 };
        productMap.set(name, { quantity: ex.quantity + (item.quantity || 0), revenue: ex.revenue + (item.totalPrice || 0) });
      }
    }

    const totalLostRevenue = orders.reduce((s, o) => s + o.totalAmount, 0);
    const totalReturned    = orders.filter(o => o.status === 'Returned').length;
    const totalCancelled   = orders.filter(o => o.status === 'Cancelled').length;
    const totalRefunded    = orders.filter(o => o.status === 'Refunded').length;
    const totalAllOrders   = snapshot.size;
    const returnRate       = totalAllOrders > 0 ? ((totalReturned + totalRefunded) / totalAllOrders) * 100 : 0;
    const cancellationRate = totalAllOrders > 0 ? (totalCancelled / totalAllOrders) * 100 : 0;

    return { orders, summary: { totalReturned, totalCancelled, totalRefunded, totalOrders: orders.length, totalLostRevenue, returnRate: Math.round(returnRate * 100) / 100, cancellationRate: Math.round(cancellationRate * 100) / 100, topReturnedProducts: Array.from(productMap.entries()).map(([productName, d]) => ({ productName, ...d })).sort((a, b) => b.revenue - a.revenue).slice(0, 10) } };
  }

  // ── Profit & Loss helpers ────────────────────────────────────────────────
  // Average calendar days in one occurrence of a recurring cost's frequency,
  // derived from OCCURRENCES_PER_YEAR (cost.ts) so the two never drift apart.
  private static daysInPeriod(frequency: RecurrenceFrequency): number {
    return 365 / OCCURRENCES_PER_YEAR[frequency];
  }

  private static daysBetweenInclusive(start: Date, end: Date): number {
    return Math.floor((end.getTime() - start.getTime()) / 86_400_000) + 1;
  }

  /**
   * Profit & Loss for [filters.dateFrom, filters.dateTo] (required — a
   * recurring cost can only be prorated against a bounded range).
   *
   * revenue / COGS come from delivered orders' line items:
   *   revenue = sum(items[].totalPrice)
   *   cogs    = sum(items[].quantity * items[].unitCostAtSale)
   * (unitCostAtSale is the weighted-average cost snapshotted onto the order
   * item at the moment stock was deducted — see WarehouseInventoryService /
   * orderService / the reserveStockOnOrderCreate Cloud Function. Orders
   * placed before that snapshot existed will show 0 cost for those lines.)
   *
   * Operating costs come from the Costs feature:
   *   - one-time costs count in full if incurredDate falls in the range.
   *   - recurring costs are prorated: dailyRate = amount / daysInPeriod(freq),
   *     multiplied by the number of days the cost's active window
   *     (incurredDate..endDate, capped to the report range) overlaps the
   *     report range. Paused recurring costs are excluded — there's no
   *     recorded "paused since" date, so we can't know which days within
   *     the range they were actually active.
   */
  private static async generateProfitLossReport(filters: ReportFilters): Promise<ProfitLossReportData> {
    if (!filters.dateFrom || !filters.dateTo) {
      throw new Error('Profit & Loss report requires both a start and end date.');
    }
    const rangeStart = filters.dateFrom;
    const rangeEnd = filters.dateTo;

    // ── Revenue + COGS from delivered orders in range ──────────────────────
    const ordersRef = collection(db, ORDERS_COLLECTION);
    const snapshot = await getDocs(query(
      ordersRef,
      where('orderDate', '>=', Timestamp.fromDate(rangeStart)),
      where('orderDate', '<=', Timestamp.fromDate(rangeEnd)),
      orderBy('orderDate', 'desc'),
    ));

    const productMap = new Map<string, ProfitLossProductItem>();
    let revenue = 0;
    let cogs = 0;
    let totalOrders = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (!this.isDelivered(data.status)) continue;
      totalOrders++;

      for (const item of (data.items || [])) {
        const itemRevenue = item.totalPrice || 0;
        const itemCogs = (item.quantity || 0) * (item.unitCostAtSale || 0);
        revenue += itemRevenue;
        cogs += itemCogs;

        const pid = item.productId || 'unknown';
        const existing = productMap.get(pid) || {
          productId: pid,
          productName: item.productName || 'Unknown Product',
          quantitySold: 0, revenue: 0, cogs: 0, grossProfit: 0,
        };
        existing.quantitySold += item.quantity || 0;
        existing.revenue += itemRevenue;
        existing.cogs += itemCogs;
        existing.grossProfit = existing.revenue - existing.cogs;
        productMap.set(pid, existing);
      }
    }

    const revenueByProduct = Array.from(productMap.values()).sort((a, b) => b.grossProfit - a.grossProfit);
    const grossProfit = revenue - cogs;

    // ── Operating costs from the Costs feature, prorated into the range ────
    const costsSnapshot = await getDocs(collection(db, COSTS_COLLECTION));
    const costBreakdownMap = new Map<string, ProfitLossCostItem>();
    let operatingCostsOneTime = 0;
    let operatingCostsRecurring = 0;

    costsSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const category: string = data.category || 'other';
      const amount: number = data.amount || 0;
      const incurredDate: Date | undefined = data.incurredDate?.toDate();
      if (!incurredDate) return;

      let contributed = 0;

      if (data.costType === CostType.ONE_TIME) {
        if (incurredDate >= rangeStart && incurredDate <= rangeEnd) {
          contributed = amount;
          operatingCostsOneTime += contributed;
        }
      } else if (data.costType === CostType.RECURRING) {
        if (data.status === CostStatus.PAUSED) return;
        const frequency: RecurrenceFrequency | undefined = data.recurrenceFrequency;
        if (!frequency) return;

        const costEnd: Date | undefined = data.endDate?.toDate();
        const overlapStart = incurredDate > rangeStart ? incurredDate : rangeStart;
        const overlapEnd = costEnd && costEnd < rangeEnd ? costEnd : rangeEnd;

        if (overlapEnd >= overlapStart) {
          const overlapDays = this.daysBetweenInclusive(overlapStart, overlapEnd);
          const dailyRate = amount / this.daysInPeriod(frequency);
          contributed = dailyRate * overlapDays;
          operatingCostsRecurring += contributed;
        }
      }

      if (contributed > 0) {
        const existing = costBreakdownMap.get(category) || {
          category: COST_CATEGORY_LABELS[category as keyof typeof COST_CATEGORY_LABELS] || category,
          amount: 0, oneTimeAmount: 0, recurringAmount: 0, count: 0,
        };
        existing.amount += contributed;
        if (data.costType === CostType.ONE_TIME) existing.oneTimeAmount += contributed;
        else existing.recurringAmount += contributed;
        existing.count += 1;
        costBreakdownMap.set(category, existing);
      }
    });

    const costBreakdown = Array.from(costBreakdownMap.values()).sort((a, b) => b.amount - a.amount);
    const operatingCosts = operatingCostsOneTime + operatingCostsRecurring;
    const netProfit = grossProfit - operatingCosts;
    const periodDays = this.daysBetweenInclusive(rangeStart, rangeEnd);

    return {
      revenueByProduct,
      costBreakdown,
      summary: {
        totalRecords: revenueByProduct.length,
        periodDays,
        totalOrders,
        revenue,
        cogs,
        grossProfit,
        grossMarginPercent: revenue > 0 ? (grossProfit / revenue) * 100 : 0,
        operatingCosts,
        operatingCostsOneTime,
        operatingCostsRecurring,
        netProfit,
        netMarginPercent: revenue > 0 ? (netProfit / revenue) * 100 : 0,
      },
    };
  }

  // ── Month bucket key helper (YYYY-MM) ──────────────────────────────────────
  private static monthKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Cost Report — consolidates every place cost is captured:
   *   1. Operating costs (Costs feature), prorated into [dateFrom, dateTo]
   *      exactly the way the Profit & Loss report does.
   *   2. Purchase orders / imports — money spent buying inventory from suppliers.
   *   3. COGS — cost of goods actually sold, from delivered orders.
   * A date range is required so recurring costs can be prorated.
   */
  private static async generateCostReport(filters: ReportFilters): Promise<CostReportData> {
    if (!filters.dateFrom || !filters.dateTo) {
      throw new Error('Cost report requires both a start and end date.');
    }
    const rangeStart = filters.dateFrom;
    const rangeEnd = filters.dateTo;

    // ── 1. Operating costs (costs collection) ──────────────────────────────
    const costsSnapshot = await getDocs(collection(db, COSTS_COLLECTION));
    const categoryMap = new Map<string, CostCategoryItem>();
    const timelineMap = new Map<string, CostTimelineItem>();
    let operatingCostsOneTime = 0;
    let operatingCostsRecurring = 0;

    const addTimeline = (key: string, field: 'operatingCosts' | 'purchaseCosts', amount: number) => {
      const ex = timelineMap.get(key) || { period: key, operatingCosts: 0, purchaseCosts: 0, total: 0 };
      ex[field] += amount;
      ex.total = ex.operatingCosts + ex.purchaseCosts;
      timelineMap.set(key, ex);
    };

    costsSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const category: string = data.category || 'other';
      const amount: number = data.amount || 0;
      const incurredDate: Date | undefined = data.incurredDate?.toDate();
      if (!incurredDate) return;

      let contributed = 0;

      if (data.costType === CostType.ONE_TIME) {
        if (incurredDate >= rangeStart && incurredDate <= rangeEnd) {
          contributed = amount;
          operatingCostsOneTime += contributed;
          addTimeline(this.monthKey(incurredDate), 'operatingCosts', contributed);
        }
        if (contributed > 0) {
          const ex = categoryMap.get(category) || {
            category: COST_CATEGORY_LABELS[category as keyof typeof COST_CATEGORY_LABELS] || category,
            amount: 0, oneTimeAmount: 0, recurringAmount: 0, count: 0, percentageOfOperating: 0,
          };
          ex.amount += contributed; ex.oneTimeAmount += contributed; ex.count += 1;
          categoryMap.set(category, ex);
        }
      } else if (data.costType === CostType.RECURRING) {
        if (data.status === CostStatus.PAUSED) return;
        const frequency: RecurrenceFrequency | undefined = data.recurrenceFrequency;
        if (!frequency) return;

        const costEnd: Date | undefined = data.endDate?.toDate();
        const overlapStart = incurredDate > rangeStart ? incurredDate : rangeStart;
        const overlapEnd = costEnd && costEnd < rangeEnd ? costEnd : rangeEnd;

        if (overlapEnd >= overlapStart) {
          const overlapDays = this.daysBetweenInclusive(overlapStart, overlapEnd);
          const dailyRate = amount / this.daysInPeriod(frequency);
          contributed = dailyRate * overlapDays;
          operatingCostsRecurring += contributed;

          const ex = categoryMap.get(category) || {
            category: COST_CATEGORY_LABELS[category as keyof typeof COST_CATEGORY_LABELS] || category,
            amount: 0, oneTimeAmount: 0, recurringAmount: 0, count: 0, percentageOfOperating: 0,
          };
          ex.amount += contributed; ex.recurringAmount += contributed; ex.count += 1;
          categoryMap.set(category, ex);

          // Spread recurring cost across the months it overlaps (simple even split).
          const cursor = new Date(overlapStart);
          const monthContribution = new Map<string, number>();
          for (let day = 0; day < overlapDays; day++) {
            const k = this.monthKey(cursor);
            monthContribution.set(k, (monthContribution.get(k) || 0) + dailyRate);
            cursor.setDate(cursor.getDate() + 1);
          }
          monthContribution.forEach((amt, k) => addTimeline(k, 'operatingCosts', amt));
        }
      }
    });

    const operatingCosts = operatingCostsOneTime + operatingCostsRecurring;
    const costByCategory = Array.from(categoryMap.values())
      .map(c => ({ ...c, percentageOfOperating: operatingCosts > 0 ? (c.amount / operatingCosts) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount);

    // ── 2. Purchase orders / imports ───────────────────────────────────────
    const poConstraints: any[] = [];
    if (filters.dateFrom) poConstraints.push(where('createdAt', '>=', Timestamp.fromDate(rangeStart)));
    if (filters.dateTo)   poConstraints.push(where('createdAt', '<=', Timestamp.fromDate(rangeEnd)));
    let poSnapshot;
    try {
      poSnapshot = await getDocs(query(collection(db, PURCHASE_ORDERS_COLLECTION), ...poConstraints, orderBy('createdAt', 'desc')));
    } catch {
      // Fallback if createdAt isn't indexed/queryable — fetch all, filter client-side.
      poSnapshot = await getDocs(collection(db, PURCHASE_ORDERS_COLLECTION));
    }

    const supplierMap = new Map<string, CostSupplierItem>();
    let purchaseCosts = 0;
    let purchaseCostsPaid = 0;
    const cancelledPoStatuses = ['cancelled', 'Cancelled', 'draft', 'Draft'];

    poSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const created: Date | undefined = data.createdAt?.toDate();
      if (created && (created < rangeStart || created > rangeEnd)) return;
      if (cancelledPoStatuses.includes(data.status)) return;

      const total = data.totalAmount || 0;
      const paid = data.paidAmount || 0;
      purchaseCosts += total;
      purchaseCostsPaid += paid;

      const sid = data.supplierId || 'unknown';
      const ex = supplierMap.get(sid) || {
        supplierId: sid, supplierName: data.supplierName || 'Unknown Supplier',
        poCount: 0, totalPurchased: 0, totalPaid: 0, outstanding: 0,
      };
      ex.poCount += 1;
      ex.totalPurchased += total;
      ex.totalPaid += paid;
      ex.outstanding += Math.max(0, total - paid);
      supplierMap.set(sid, ex);

      if (created) addTimeline(this.monthKey(created), 'purchaseCosts', total);
    });

    const costBySupplier = Array.from(supplierMap.values()).sort((a, b) => b.totalPurchased - a.totalPurchased);
    const purchaseCostsOutstanding = costBySupplier.reduce((s, x) => s + x.outstanding, 0);

    // ── 3. COGS from delivered orders in range ─────────────────────────────
    let cogs = 0;
    const ordersSnapshot = await getDocs(query(
      collection(db, ORDERS_COLLECTION),
      where('orderDate', '>=', Timestamp.fromDate(rangeStart)),
      where('orderDate', '<=', Timestamp.fromDate(rangeEnd)),
      orderBy('orderDate', 'desc'),
    ));
    ordersSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (!this.isDelivered(data.status)) return;
      for (const item of (data.items || [])) {
        cogs += (item.quantity || 0) * (item.unitCostAtSale || 0);
      }
    });

    const costTimeline = Array.from(timelineMap.values()).sort((a, b) => a.period.localeCompare(b.period));
    const totalCost = operatingCosts + purchaseCosts;

    const costTypeBreakdown = [
      { label: 'Operating Costs', amount: operatingCosts },
      { label: 'Purchases / Imports', amount: purchaseCosts },
      { label: 'COGS (goods sold)', amount: cogs },
    ].filter(x => x.amount > 0);

    return {
      costByCategory,
      costBySupplier,
      costTimeline,
      costTypeBreakdown,
      summary: {
        totalRecords: costByCategory.length + costBySupplier.length,
        periodDays: this.daysBetweenInclusive(rangeStart, rangeEnd),
        operatingCosts,
        operatingCostsOneTime,
        operatingCostsRecurring,
        purchaseCosts,
        purchaseCostsPaid,
        purchaseCostsOutstanding,
        cogs,
        totalCost,
        topCostCategory: costByCategory[0]?.category || 'N/A',
        topCostSupplier: costBySupplier[0]?.supplierName || 'N/A',
      },
    };
  }

  /**
   * Payment & Due / Arrears Report — every place money is captured:
   *   • Incoming — customer order payments (received vs. still due)
   *   • Outgoing — supplier / purchase-order payments (paid vs. arrears)
   */
  private static async generatePaymentsReport(filters: ReportFilters): Promise<PaymentReportData> {
    const rangeStart = filters.dateFrom;
    const rangeEnd = filters.dateTo;
    const inRange = (d?: Date) => {
      if (!d) return true;
      if (rangeStart && d < rangeStart) return false;
      if (rangeEnd && d > rangeEnd) return false;
      return true;
    };

    // ── Customer order payments ────────────────────────────────────────────
    const orderConstraints: any[] = [];
    if (rangeStart) orderConstraints.push(where('orderDate', '>=', Timestamp.fromDate(rangeStart)));
    if (rangeEnd)   orderConstraints.push(where('orderDate', '<=', Timestamp.fromDate(rangeEnd)));
    orderConstraints.push(orderBy('orderDate', 'desc'));
    const ordersSnapshot = await getDocs(query(collection(db, ORDERS_COLLECTION), ...orderConstraints));

    const methodMap = new Map<string, PaymentMethodItem>();
    const customerDues: CustomerDueItem[] = [];
    const timelineMap = new Map<string, PaymentTimelineItem>();
    let totalReceived = 0;
    let totalCustomerDue = 0;

    const addTimeline = (key: string, field: 'received' | 'paidOut', amount: number) => {
      const ex = timelineMap.get(key) || { period: key, received: 0, paidOut: 0 };
      ex[field] += amount;
      timelineMap.set(key, ex);
    };

    const cancelledOrderStatuses = ['cancelled', 'Cancelled'];

    ordersSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (cancelledOrderStatuses.includes(data.status)) return;
      const orderDate: Date = data.orderDate?.toDate() || new Date();
      const total = data.totalAmount || 0;
      const isPaid = ['paid', 'Paid'].includes(data.paymentStatus);
      // paidAmount if tracked, else infer from paymentStatus
      const paid = data.paidAmount !== undefined ? data.paidAmount : (isPaid ? total : 0);
      const due = Math.max(0, total - paid);

      totalReceived += paid;
      totalCustomerDue += due;

      if (paid > 0) {
        const method = this.formatPaymentMethod(data.paymentMethod || 'unknown');
        const ex = methodMap.get(method) || { method, orderCount: 0, amount: 0, percentageOfReceived: 0 };
        ex.orderCount += 1;
        ex.amount += paid;
        methodMap.set(method, ex);
        addTimeline(this.monthKey(orderDate), 'received', paid);
      }

      if (due > 0) {
        customerDues.push({
          orderId: docSnap.id,
          orderNumber: data.orderNumber || 'N/A',
          customerName: data.customerName || 'Unknown',
          totalAmount: total,
          paidAmount: paid,
          due,
          paymentStatus: this.formatStatus(data.paymentStatus || 'pending'),
          date: orderDate,
        });
      }
    });

    const incomingByMethod = Array.from(methodMap.values())
      .map(m => ({ ...m, percentageOfReceived: totalReceived > 0 ? (m.amount / totalReceived) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount);

    // ── Supplier / purchase-order payments ─────────────────────────────────
    let poSnapshot;
    try {
      poSnapshot = await getDocs(query(collection(db, PURCHASE_ORDERS_COLLECTION), orderBy('createdAt', 'desc')));
    } catch {
      poSnapshot = await getDocs(collection(db, PURCHASE_ORDERS_COLLECTION));
    }

    const supplierArrears: SupplierArrearItem[] = [];
    let totalPaidToSuppliers = 0;
    let totalSupplierArrears = 0;
    const excludedPoStatuses = ['cancelled', 'Cancelled', 'draft', 'Draft'];

    poSnapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const created: Date | undefined = data.createdAt?.toDate();
      if (!inRange(created)) return;
      if (excludedPoStatuses.includes(data.status)) return;

      const total = data.totalAmount || 0;
      const paid = data.paidAmount || 0;
      const outstanding = Math.max(0, total - paid);

      totalPaidToSuppliers += paid;
      totalSupplierArrears += outstanding;

      if (paid > 0 && created) addTimeline(this.monthKey(created), 'paidOut', paid);

      if (outstanding > 0) {
        supplierArrears.push({
          poId: docSnap.id,
          poNumber: data.poNumber || 'N/A',
          supplierName: data.supplierName || 'Unknown Supplier',
          totalAmount: total,
          paidAmount: paid,
          outstanding,
          status: this.formatStatus(data.paymentStatus || 'unpaid'),
          date: created || new Date(),
        });
      }
    });

    supplierArrears.sort((a, b) => b.outstanding - a.outstanding);
    customerDues.sort((a, b) => b.due - a.due);

    const paymentTimeline = Array.from(timelineMap.values()).sort((a, b) => a.period.localeCompare(b.period));

    return {
      incomingByMethod,
      supplierArrears,
      customerDues,
      paymentTimeline,
      summary: {
        totalRecords: incomingByMethod.length + supplierArrears.length + customerDues.length,
        totalReceived,
        totalCustomerDue,
        customerDueCount: customerDues.length,
        totalPaidToSuppliers,
        totalSupplierArrears,
        supplierArrearsCount: supplierArrears.length,
        netCashPosition: totalReceived - totalPaidToSuppliers,
      },
    };
  }

  // ── AI-powered forecast report ─────────────────────────────────────────
  //
  // Historical data is gathered here (client-side, same as every other
  // report) from the last FORECAST_LOOKBACK_DAYS of orders. The actual
  // prediction reasoning — combining that history with general e-commerce/
  // global-trend knowledge — happens in the `generateForecast` Cloud
  // Function (Gemini), which returns predictions + a confidence assessment
  // + a written AIInsights section (summary/findings/recommendations/
  // risks/opportunities) for the report.
  private static readonly FORECAST_LOOKBACK_DAYS = 90;

  private static async generateForecastReport(
    filters: ReportFilters
  ): Promise<{ report: ForecastReportData; aiInsights: AIInsights }> {
    const forecastType = filters.forecastType || 'revenue';
    const forecastPeriod = Math.min(90, Math.max(1, filters.forecastPeriod || 30));

    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - this.FORECAST_LOOKBACK_DAYS * 86_400_000);

    const ordersRef = collection(db, ORDERS_COLLECTION);
    const snapshot = await getDocs(
      query(
        ordersRef,
        where('orderDate', '>=', Timestamp.fromDate(startDate)),
        where('orderDate', '<=', Timestamp.fromDate(endDate)),
        orderBy('orderDate', 'asc')
      )
    );

    // Seed every day in the lookback window with 0 so the series has no gaps.
    const dailyMap = new Map<string, number>();
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dailyMap.set(d.toISOString().split('T')[0], 0);
    }

    const metricLabel = forecastType === 'revenue' ? 'revenue' : forecastType === 'sales' ? 'orders' : 'units sold';

    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      const orderDate = data.orderDate?.toDate();
      if (!orderDate) return;
      const dateKey = orderDate.toISOString().split('T')[0];
      if (!dailyMap.has(dateKey)) return;

      if (forecastType === 'revenue') {
        if (this.isDelivered(data.status)) {
          dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + (data.totalAmount || 0));
        }
      } else if (forecastType === 'sales') {
        dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + 1);
      } else {
        // 'inventory' forecast: no historical stock-level snapshots exist, so we use
        // daily units sold as a demand proxy — a realistic basis for restock planning.
        const units = (data.items || []).reduce((s: number, it: any) => s + (it.quantity || 0), 0);
        dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + units);
      }
    });

    const dataPoints = Array.from(dailyMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateStr, value]) => ({ date: new Date(dateStr), value, metric: metricLabel }));

    if (dataPoints.every(p => p.value === 0)) {
      throw new Error('Not enough historical data yet to generate a forecast.');
    }

    // Rough local trend estimate (first half vs. second half average) — sent to the
    // AI as a fallback/sanity check; the AI's own trend assessment takes precedence.
    const half = Math.floor(dataPoints.length / 2);
    const firstHalfAvg = dataPoints.slice(0, half).reduce((s, p) => s + p.value, 0) / Math.max(1, half);
    const secondHalfAvg = dataPoints.slice(half).reduce((s, p) => s + p.value, 0) / Math.max(1, dataPoints.length - half);
    const localTrendPct = firstHalfAvg > 0 ? ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100 : 0;

    const generateForecastFn = httpsCallable<
      { forecastType: 'sales' | 'inventory' | 'revenue'; forecastPeriod: number; historicalData: { date: string; value: number }[] },
      any
    >(functions, 'generateForecast');

    const result = await generateForecastFn({
      forecastType,
      forecastPeriod,
      historicalData: dataPoints.map(p => ({ date: p.date.toISOString().split('T')[0], value: Math.round(p.value * 100) / 100 })),
    });

    const ai = result.data;
    const lastHistoricalDate = dataPoints[dataPoints.length - 1].date;

    const predictions: ForecastPrediction[] = (ai.predictions || []).map((p: any) => ({
      date: new Date(lastHistoricalDate.getTime() + p.dayOffset * 86_400_000),
      predictedValue: p.predictedValue,
      lowerBound: p.lowerBound,
      upperBound: p.upperBound,
      confidence: p.confidence,
      factors: [],
    }));

    const totalPredicted = predictions.reduce((s, p) => s + p.predictedValue, 0);
    const averagePredictedValue = predictions.length > 0 ? totalPredicted / predictions.length : 0;
    const peak = predictions.reduce((mx, p) => (p.predictedValue > mx.predictedValue ? p : mx), predictions[0]);
    const low = predictions.reduce((mn, p) => (p.predictedValue < mn.predictedValue ? p : mn), predictions[0]);

    const report: ForecastReportData = {
      forecastType,
      forecastPeriod,
      historicalData: {
        startDate,
        endDate,
        dataPoints,
        trends: {
          direction: ai.trend?.direction || (localTrendPct > 2 ? 'increasing' : localTrendPct < -2 ? 'decreasing' : 'stable'),
          percentage: ai.trend?.percentage ?? Math.round(localTrendPct * 100) / 100,
          description: ai.trend?.description || '',
        },
      },
      predictions,
      summary: {
        totalForecastPeriod: forecastPeriod,
        averagePredictedValue,
        expectedGrowth: ai.trend?.percentage ?? Math.round(localTrendPct * 100) / 100,
        peakDate: peak ? peak.date.toLocaleDateString() : '',
        peakValue: peak ? peak.predictedValue : 0,
        lowDate: low ? low.date.toLocaleDateString() : '',
        lowValue: low ? low.predictedValue : 0,
        totalPredicted,
      },
      confidence: {
        level: ai.confidenceLevel || 'medium',
        score: ai.confidenceScore ?? 50,
        factors: ai.confidenceFactors || [],
      },
    };

    const aiInsights: AIInsights = {
      summary: ai.aiInsights?.summary || '',
      keyFindings: ai.aiInsights?.keyFindings || [],
      recommendations: ai.aiInsights?.recommendations || [],
      trends: ai.aiInsights?.trends || [],
      riskFactors: ai.aiInsights?.riskFactors || [],
      opportunities: ai.aiInsights?.opportunities || [],
      generatedAt: new Date(),
    };

    return { report, aiInsights };
  }

  /** @deprecated Use getReportsPaginated */
  static async getReports(): Promise<Report[]> {
    try {
      const q = query(collection(db, REPORTS_COLLECTION), orderBy('generatedAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return { id: doc.id, ...data, generatedAt: data.generatedAt?.toDate(), filters: this.deserializeFilters(data.filters || {}), data: this.deserializeReportData(data.data || {}) } as Report;
      });
    } catch (error) { console.error('Error fetching reports:', error); throw error; }
  }

  static async getReport(reportId: string): Promise<Report | null> {
    try {
      const snap = await getDoc(doc(db, REPORTS_COLLECTION, reportId));
      if (!snap.exists()) return null;
      const data = snap.data();
      return { id: snap.id, ...data, generatedAt: data.generatedAt?.toDate(), filters: this.deserializeFilters(data.filters || {}), data: this.deserializeReportData(data.data || {}) } as Report;
    } catch (error) { console.error('Error fetching report:', error); throw error; }
  }

  static async deleteReport(reportId: string): Promise<{ success: boolean; message: string }> {
    try {
      await deleteDoc(doc(db, REPORTS_COLLECTION, reportId));
      return { success: true, message: 'Report deleted successfully.' };
    } catch (error: any) {
      console.error('Error deleting report:', error);
      return { success: false, message: error.message || 'Failed to delete report.' };
    }
  }

  static async getReportStats(): Promise<ReportStats> {
    try {
      const col = collection(db, REPORTS_COLLECTION);

      const [totalSnap, completedSnap, generatingSnap, failedSnap, allDocsSnap] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('status', '==', 'completed'))),
        getCountFromServer(query(col, where('status', '==', 'generating'))),
        getCountFromServer(query(col, where('status', '==', 'failed'))),
        getDocs(query(col)),
      ]);

      const byType: { [k: string]: number } = {};
      allDocsSnap.docs.forEach(d => {
        const t = d.data().reportType as string;
        if (t) byType[t] = (byType[t] || 0) + 1;
      });

      return {
        total:      totalSnap.data().count,
        completed:  completedSnap.data().count,
        generating: generatingSnap.data().count,
        failed:     failedSnap.data().count,
        byType,
      };
    } catch (error) {
      console.error('Error getting report stats:', error);
      throw error;
    }
  }
}

// import {
//   collection,
//   doc,
//   addDoc,
//   updateDoc,
//   deleteDoc,
//   getDocs,
//   getDoc,
//   query,
//   where,
//   orderBy,
//   serverTimestamp,
//   Timestamp,
//   limit,
//   startAfter,
//   endBefore,
//   limitToLast,
//   getCountFromServer,
//   QueryDocumentSnapshot,
//   DocumentData,
//   Query,
// } from 'firebase/firestore';
// import { db } from '../../../firebase/config';
// import {
//   Report,
//   ReportType,
//   ReportFilters,
//   SalesReportData,
//   InventoryReportData,
//   PurchaseOrderReportData,
//   LowStockReportData,
//   LowStockItem,
//   WarehousePerformanceData,
//   ProductPerformanceData,
//   RevenueReportData,
//   RevenueDayItem,
//   ReturnsCancellationsReportData,
//   ReturnCancelOrderItem,
// } from './Report';
// import { WarehouseInventoryService } from '../product/WarehouseInventoryService';
// import { WarehouseService } from '../warehouse/warehouseService';

// const REPORTS_COLLECTION       = 'reports';
// const ORDERS_COLLECTION        = 'orders';
// const PRODUCTS_COLLECTION      = 'products';
// const PURCHASE_ORDERS_COLLECTION = 'purchaseOrders';
// const WAREHOUSES_COLLECTION    = 'warehouses';
// const CATEGORIES_COLLECTION    = 'categories';

// // Statuses considered "completed / revenue-generating" (for revenue & product performance)
// const DELIVERED_STATUSES = ['delivered', 'Delivered'];
// // Statuses that represent returns / cancellations
// const RETURN_CANCEL_STATUSES = ['returned', 'Returned', 'cancelled', 'Cancelled', 'refunded', 'Refunded'];

// export interface PaginatedReportsResult {
//   reports: Report[];
//   totalCount: number;
//   lastVisible: QueryDocumentSnapshot<DocumentData> | null;
//   firstVisible: QueryDocumentSnapshot<DocumentData> | null;
// }

// export class ReportService {

//   // ─── helpers ────────────────────────────────────────────────────────────────

//   private static formatPaymentMethod(method: string): string {
//     const map: { [k: string]: string } = {
//       bank_transfer: 'Bank Transfer', credit_card: 'Credit Card', debit_card: 'Debit Card',
//       cash_on_delivery: 'Cash on Delivery', paypal: 'PayPal', stripe: 'Stripe', cod: 'Cash on Delivery',
//     };
//     return map[method] || method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
//   }

//   private static formatStatus(status: string): string {
//     const map: { [k: string]: string } = {
//       pending: 'Pending', processing: 'Processing', confirmed: 'Confirmed', shipped: 'Shipped',
//       delivered: 'Delivered', cancelled: 'Cancelled', returned: 'Returned', refunded: 'Refunded',
//       draft: 'Draft', approved: 'Approved', received: 'Received', paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid',
//     };
//     return map[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
//   }

//   private static isDelivered(status: string): boolean {
//     return DELIVERED_STATUSES.includes(status);
//   }

//   private static isReturnOrCancel(status: string): boolean {
//     return RETURN_CANCEL_STATUSES.includes(status);
//   }

//   // ─── paginated list ──────────────────────────────────────────────────────────

//   static async getReportsPaginated(
//     pageSize = 10,
//     startAfterDoc: QueryDocumentSnapshot<DocumentData> | null = null,
//     endBeforeDoc: QueryDocumentSnapshot<DocumentData> | null = null,
//     direction: 'next' | 'prev' | 'first' = 'first',
//     searchQuery?: string,
//     typeFilter?: ReportType,
//     statusFilter?: 'completed' | 'generating' | 'failed'
//   ): Promise<PaginatedReportsResult> {
//     try {
//       const reports: Report[] = [];
//       const collectionRef = collection(db, REPORTS_COLLECTION);

//       let queryConstraints: any[] = [orderBy('generatedAt', 'desc')];
//       if (typeFilter)   queryConstraints.push(where('reportType', '==', typeFilter));
//       if (statusFilter) queryConstraints.push(where('status',     '==', statusFilter));

//       const countQuery    = query(collectionRef, ...queryConstraints.filter(c => c.type === 'where'));
//       const countSnapshot = await getCountFromServer(countQuery);
//       const totalCount    = countSnapshot.data().count;

//       let q: Query<DocumentData>;
//       if (direction === 'prev' && endBeforeDoc) {
//         q = query(collectionRef, ...queryConstraints, endBefore(endBeforeDoc), limitToLast(pageSize));
//       } else if (direction === 'next' && startAfterDoc) {
//         q = query(collectionRef, ...queryConstraints, startAfter(startAfterDoc), limit(pageSize));
//       } else {
//         q = query(collectionRef, ...queryConstraints, limit(pageSize));
//       }

//       const snapshot = await getDocs(q);
//       let lastVisible:  QueryDocumentSnapshot<DocumentData> | null = null;
//       let firstVisible: QueryDocumentSnapshot<DocumentData> | null = null;
//       if (!snapshot.empty) { firstVisible = snapshot.docs[0]; lastVisible = snapshot.docs[snapshot.docs.length - 1]; }

//       snapshot.docs.forEach(docSnap => {
//         const data = docSnap.data();
//         const report: Report = {
//           id: docSnap.id, reportType: data.reportType, reportName: data.reportName,
//           description: data.description || '', filters: this.deserializeFilters(data.filters || {}),
//           status: data.status, generatedAt: data.generatedAt?.toDate() || new Date(),
//           generatedBy: data.generatedBy, generatedByName: data.generatedByName,
//           data: this.deserializeReportData(data.data || {}), summary: data.summary || { totalRecords: 0 }, error: data.error,
//         };
//         if (searchQuery && searchQuery.trim() !== '') {
//           const lower = searchQuery.toLowerCase();
//           const matches = report.reportName.toLowerCase().includes(lower) ||
//             (report.description?.toLowerCase().includes(lower)) ||
//             (report.generatedByName?.toLowerCase().includes(lower));
//           if (matches) reports.push(report);
//         } else {
//           reports.push(report);
//         }
//       });

//       return { reports, totalCount, lastVisible, firstVisible };
//     } catch (error) {
//       console.error('Error fetching paginated reports:', error);
//       throw error;
//     }
//   }

//   // ─── generate ────────────────────────────────────────────────────────────────

//   static async generateReport(
//     reportType: ReportType, reportName: string, description: string,
//     filters: ReportFilters, userId: string, userName: string
//   ): Promise<string> {
//     try {
//       const reportRef = await addDoc(collection(db, REPORTS_COLLECTION), {
//         reportType, reportName, description, filters: this.serializeFilters(filters),
//         status: 'generating', generatedAt: serverTimestamp(),
//         generatedBy: userId, generatedByName: userName, data: {}, summary: { totalRecords: 0 },
//       });
//       const reportId = reportRef.id;

//       try {
//         let data: any, summary: any;
//         switch (reportType) {
//           case 'sales':                 { const d = await this.generateSalesReport(filters);                data = d; summary = d.summary; break; }
//           case 'inventory':             { const d = await this.generateInventoryReport(filters);            data = d; summary = d.summary; break; }
//           case 'purchase_orders':       { const d = await this.generatePurchaseOrderReport(filters);        data = d; summary = d.summary; break; }
//           case 'low_stock':             { const d = await this.generateLowStockReport(filters);             data = d; summary = d.summary; break; }
//           case 'warehouse_performance': { const d = await this.generateWarehousePerformanceReport(filters); data = d; summary = d.summary; break; }
//           case 'product_performance':   { const d = await this.generateProductPerformanceReport(filters);   data = d; summary = d.summary; break; }
//           case 'revenue':               { const d = await this.generateRevenueReport(filters);              data = d; summary = d.summary; break; }
//           case 'returns_cancellations': { const d = await this.generateReturnsCancellationsReport(filters); data = d; summary = d.summary; break; }
//           default: throw new Error('Invalid report type');
//         }

//         await updateDoc(reportRef, {
//           status: 'completed', data: this.serializeReportData(data),
//           summary: {
//             ...summary,
//             totalRecords:
//               data.purchaseOrders?.length || data.orders?.length || data.products?.length ||
//               data.items?.length || data.warehouses?.length || data.dailyRevenue?.length || 0,
//           },
//         });
//         return reportId;
//       } catch (error) {
//         await updateDoc(reportRef, { status: 'failed', error: error instanceof Error ? error.message : 'Unknown error' });
//         throw error;
//       }
//     } catch (error) {
//       console.error('Error generating report:', error);
//       throw error;
//     }
//   }

//   // ─── serialization ───────────────────────────────────────────────────────────

//   private static serializeFilters(filters: ReportFilters): any {
//     return {
//       dateFrom: filters.dateFrom ? Timestamp.fromDate(filters.dateFrom) : null,
//       dateTo:   filters.dateTo   ? Timestamp.fromDate(filters.dateTo)   : null,
//       warehouseId: filters.warehouseId || null, categoryId: filters.categoryId || null,
//       subcategoryId: filters.subcategoryId || null, productId: filters.productId || null,
//       status: filters.status || null, supplierId: filters.supplierId || null,
//     };
//   }

//   private static deserializeFilters(filters: any): ReportFilters {
//     return {
//       dateFrom: filters.dateFrom?.toDate(), dateTo: filters.dateTo?.toDate(),
//       warehouseId: filters.warehouseId, categoryId: filters.categoryId,
//       subcategoryId: filters.subcategoryId, productId: filters.productId,
//       status: filters.status, supplierId: filters.supplierId,
//     };
//   }

//   private static serializeReportData(data: any): any {
//     if (!data) return data;
//     if (Array.isArray(data)) return data.map(i => this.serializeReportData(i));
//     if (typeof data === 'object' && data !== null) {
//       if (data instanceof Date) return Timestamp.fromDate(data);
//       const out: any = {};
//       for (const key in data) {
//         if (Object.prototype.hasOwnProperty.call(data, key)) {
//           const v = this.serializeReportData(data[key]);
//           if (v !== undefined) out[key] = v;
//         }
//       }
//       return out;
//     }
//     return data;
//   }

//   private static deserializeReportData(data: any): any {
//     if (!data) return data;
//     if (Array.isArray(data)) return data.map(i => this.deserializeReportData(i));
//     if (typeof data === 'object' && data !== null) {
//       if (data.toDate && typeof data.toDate === 'function') return data.toDate();
//       const out: any = {};
//       for (const key in data) {
//         if (Object.prototype.hasOwnProperty.call(data, key)) out[key] = this.deserializeReportData(data[key]);
//       }
//       return out;
//     }
//     return data;
//   }

//   private static async getCategoryName(categoryId: string): Promise<string> {
//     try {
//       const snap = await getDoc(doc(db, CATEGORIES_COLLECTION, categoryId));
//       return snap.exists() ? snap.data().name || 'Unknown' : 'Unknown';
//     } catch { return 'Unknown'; }
//   }

//   // ─── Sales Report — ALL statuses included (original logic preserved) ─────────

//   private static async generateSalesReport(filters: ReportFilters): Promise<SalesReportData> {
//     const ordersRef = collection(db, ORDERS_COLLECTION);
//     let constraints: any[] = [];
//     if (filters.dateFrom) constraints.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
//     if (filters.dateTo)   constraints.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
//     if (filters.status)   constraints.push(where('status', '==', filters.status));
//     constraints.push(orderBy('orderDate', 'desc'));

//     const snapshot = await getDocs(query(ordersRef, ...constraints));

//     // ALL orders regardless of status
//     const orders = snapshot.docs.map(d => {
//       const data = d.data();
//       return {
//         orderId: d.id,
//         orderNumber:    data.orderNumber || 'N/A',
//         orderDate:      data.orderDate?.toDate() || new Date(),
//         customerName:   data.customerName || 'Unknown',
//         customerEmail:  data.customerEmail || 'N/A',
//         items:          data.items?.length || 0,
//         subtotal:       data.subtotal || 0,
//         taxAmount:      data.taxAmount || 0,
//         shippingCost:   data.shippingCost || 0,
//         discountAmount: data.discountAmount || 0,
//         totalAmount:    data.totalAmount || 0,
//         paymentStatus:  data.paymentStatus || 'pending',
//         paymentMethod:  this.formatPaymentMethod(data.paymentMethod || 'N/A'),
//         status:         this.formatStatus(data.status || 'pending'),
//       };
//     });

//     const totalRevenue  = orders.reduce((s, o) => s + o.totalAmount, 0);
//     const totalTax      = orders.reduce((s, o) => s + o.taxAmount, 0);
//     const totalShipping = orders.reduce((s, o) => s + o.shippingCost, 0);
//     const totalDiscount = orders.reduce((s, o) => s + o.discountAmount, 0);

//     // Top products from ALL orders
//     const productMap = new Map<string, { quantity: number; revenue: number }>();
//     for (const d of snapshot.docs) {
//       for (const item of (d.data().items || [])) {
//         const name = item.productName || 'Unknown Product';
//         const ex   = productMap.get(name) || { quantity: 0, revenue: 0 };
//         productMap.set(name, { quantity: ex.quantity + (item.quantity || 0), revenue: ex.revenue + (item.totalPrice || 0) });
//       }
//     }
//     const topProducts = Array.from(productMap.entries())
//       .map(([productName, d]) => ({ productName, ...d }))
//       .sort((a, b) => b.revenue - a.revenue).slice(0, 10);

//     const statusBreakdown: { [k: string]: number } = {};
//     orders.forEach(o => { statusBreakdown[o.status] = (statusBreakdown[o.status] || 0) + 1; });

//     const paymentMethodBreakdown: { [k: string]: number } = {};
//     orders.forEach(o => { paymentMethodBreakdown[o.paymentMethod] = (paymentMethodBreakdown[o.paymentMethod] || 0) + 1; });

//     return {
//       orders,
//       summary: {
//         totalOrders: orders.length, totalRevenue, totalTax, totalShipping, totalDiscount,
//         averageOrderValue: orders.length > 0 ? totalRevenue / orders.length : 0,
//         topProducts, statusBreakdown, paymentMethodBreakdown,
//       },
//     };
//   }

//   // ─── Inventory Report ────────────────────────────────────────────────────────

//   private static async generateInventoryReport(filters: ReportFilters): Promise<InventoryReportData> {
//     const productsRef = collection(db, PRODUCTS_COLLECTION);
//     let constraints: any[] = [];
//     if (filters.categoryId)    constraints.push(where('categoryId',    '==', filters.categoryId));
//     if (filters.subcategoryId) constraints.push(where('subcategoryId', '==', filters.subcategoryId));
//     const snapshot = await getDocs(constraints.length > 0 ? query(productsRef, ...constraints) : query(productsRef));

//     const warehouses = await WarehouseService.getActiveWarehouses();
//     const products: any[] = [];
//     let totalVariants = 0, totalStock = 0, totalValue = 0;

//     for (const docSnap of snapshot.docs) {
//       const data = docSnap.data();
//       const productId = docSnap.id;
//       const categoryName    = data.categoryId    ? await this.getCategoryName(data.categoryId)    : 'Uncategorized';
//       const subcategoryName = data.subcategoryId ? await this.getCategoryName(data.subcategoryId) : '';

//       let productTotalStock = 0, productStockValue = 0;
//       const warehousesList: any[] = [], variants: any[] = [];

//       if (data.hasVariants && data.variants) {
//         totalVariants += data.variants.length;
//         for (const variant of data.variants) {
//           let variantTotalStock = 0;
//           const effectivePrice = (variant.price || 0) * (1 - (variant.discount || 0) / 100);
//           for (const warehouse of warehouses) {
//             const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId, variant.id);
//             if (inv) {
//               variantTotalStock += inv.quantity;
//               const ex = warehousesList.find(w => w.warehouseId === warehouse.id);
//               if (ex) ex.quantity += inv.quantity;
//               else warehousesList.push({ warehouseId: warehouse.id!, warehouseName: warehouse.name, quantity: inv.quantity, status: inv.isOutOfStock ? 'out_of_stock' : inv.isLowStock ? 'low_stock' : 'in_stock' });
//             }
//           }
//           variants.push({ variantId: variant.id, variantName: variant.variantSelections?.map((s: any) => s.variantValue).join(' - ') || 'Unknown', sku: variant.sku, stock: variantTotalStock, price: effectivePrice });
//           productTotalStock += variantTotalStock;
//           productStockValue += effectivePrice * variantTotalStock;
//         }
//       } else {
//         const effectivePrice = (data.price || 0) * (1 - (data.discount || 0) / 100);
//         for (const warehouse of warehouses) {
//           const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId);
//           if (inv) {
//             productTotalStock += inv.quantity;
//             warehousesList.push({ warehouseId: warehouse.id!, warehouseName: warehouse.name, quantity: inv.quantity, status: inv.isOutOfStock ? 'out_of_stock' : inv.isLowStock ? 'low_stock' : 'in_stock' });
//           }
//         }
//         productStockValue = effectivePrice * productTotalStock;
//       }

//       const isOutOfStock = productTotalStock === 0;
//       const isLowStock   = !isOutOfStock && productTotalStock <= (data.minStockLevel || 0);
//       const status       = isOutOfStock ? 'out_of_stock' : isLowStock ? 'low_stock' : 'in_stock';
//       const productItem: any = { productId, productName: data.name || 'Unknown', sku: data.sku || 'N/A', category: categoryName, subcategory: subcategoryName || '', hasVariants: data.hasVariants || false, totalStock: productTotalStock, stockValue: productStockValue, warehouses: warehousesList, status };
//       if (variants.length > 0) productItem.variants = variants;
//       products.push(productItem);
//       totalStock += productTotalStock;
//       totalValue += productStockValue;
//     }

//     return {
//       products,
//       summary: { totalProducts: products.length, totalVariants, totalStock, totalValue, lowStockItems: products.filter(p => p.status === 'low_stock').length, outOfStockItems: products.filter(p => p.status === 'out_of_stock').length, inStockItems: products.filter(p => p.status === 'in_stock').length },
//     };
//   }

//   // ─── Purchase Order Report ───────────────────────────────────────────────────

//   private static async generatePurchaseOrderReport(filters: ReportFilters): Promise<PurchaseOrderReportData> {
//     const poRef = collection(db, PURCHASE_ORDERS_COLLECTION);
//     let constraints: any[] = [];
//     if (filters.dateFrom)   constraints.push(where('createdAt',   '>=', Timestamp.fromDate(filters.dateFrom)));
//     if (filters.dateTo)     constraints.push(where('createdAt',   '<=', Timestamp.fromDate(filters.dateTo)));
//     if (filters.status)     constraints.push(where('status',       '==', filters.status));
//     if (filters.supplierId) constraints.push(where('supplierId',   '==', filters.supplierId));
//     constraints.push(orderBy('createdAt', 'desc'));

//     const snapshot = await getDocs(query(poRef, ...constraints));
//     const purchaseOrders = snapshot.docs.map(docSnap => {
//       const data = docSnap.data();
//       return {
//         poId: docSnap.id, poNumber: data.poNumber || 'N/A',
//         supplierName: data.supplierName || 'Unknown Supplier', supplierEmail: data.supplierEmail || 'N/A', supplierPhone: data.supplierPhone || 'N/A',
//         orderDate: data.createdAt?.toDate() || new Date(), expectedDelivery: data.expectedDeliveryDate?.toDate() || null,
//         actualDelivery: data.actualDeliveryDate?.toDate() || data.receivedAt?.toDate() || null,
//         approvedAt: data.approvedAt?.toDate() || null, receivedAt: data.receivedAt?.toDate() || null,
//         items: data.items?.length || 0, subtotal: data.subtotal || 0, taxAmount: data.taxAmount || 0,
//         shippingCost: data.shippingCost || 0, totalAmount: data.totalAmount || 0, paidAmount: data.paidAmount || 0,
//         status: this.formatStatus(data.status || 'draft'), paymentStatus: this.formatStatus(data.paymentStatus || 'pending'),
//         hasReturns: data.hasReturns || false,
//       };
//     });

//     const totalAmount = purchaseOrders.reduce((s, p) => s + p.totalAmount, 0);
//     const totalPaid   = purchaseOrders.reduce((s, p) => s + p.paidAmount,  0);
//     const statusBreakdown: { [k: string]: number } = {};
//     purchaseOrders.forEach(p => { statusBreakdown[p.status] = (statusBreakdown[p.status] || 0) + 1; });
//     const paymentStatusBreakdown: { [k: string]: number } = {};
//     purchaseOrders.forEach(p => { paymentStatusBreakdown[p.paymentStatus] = (paymentStatusBreakdown[p.paymentStatus] || 0) + 1; });
//     const supplierMap = new Map<string, { totalAmount: number; poCount: number }>();
//     purchaseOrders.forEach(p => { const ex = supplierMap.get(p.supplierName) || { totalAmount: 0, poCount: 0 }; supplierMap.set(p.supplierName, { totalAmount: ex.totalAmount + p.totalAmount, poCount: ex.poCount + 1 }); });

//     return {
//       purchaseOrders,
//       summary: { totalPOs: purchaseOrders.length, totalAmount, totalPaid, totalPending: totalAmount - totalPaid, statusBreakdown, paymentStatusBreakdown, topSuppliers: Array.from(supplierMap.entries()).map(([supplierName, d]) => ({ supplierName, ...d })).sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 10) },
//     };
//   }

//   // ─── Low Stock Report ────────────────────────────────────────────────────────

//   private static async generateLowStockReport(filters: ReportFilters): Promise<LowStockReportData> {
//     const items: LowStockItem[] = [];
//     const warehouses = await WarehouseService.getActiveWarehouses();
//     const snapshot   = await getDocs(collection(db, PRODUCTS_COLLECTION));

//     for (const productDoc of snapshot.docs) {
//       const productData = productDoc.data();
//       const productId   = productDoc.id;
//       const productName = productData.name || 'Unknown Product';
//       for (const warehouse of warehouses) {
//         if (productData.hasVariants && productData.variants) {
//           for (const variant of productData.variants) {
//             const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId, variant.id);
//             if (inv && (inv.isLowStock || inv.isOutOfStock)) {
//               const variantName = variant.variantSelections?.map((s: any) => s.variantValue).join(' - ') || 'Unknown';
//               const item: any = { productId, productName, sku: inv.sku || variant.sku || 'N/A', warehouseId: warehouse.id!, warehouseName: warehouse.name, currentStock: inv.quantity, minStock: inv.minStockLevel, maxStock: inv.maxStockLevel, deficit: Math.max(0, inv.minStockLevel - inv.quantity), status: inv.isOutOfStock ? 'out_of_stock' : 'low_stock' };
//               if (variant.id)  item.variantId   = variant.id;
//               if (variantName) item.variantName  = variantName;
//               items.push(item);
//             }
//           }
//         } else {
//           const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId);
//           if (inv && (inv.isLowStock || inv.isOutOfStock)) {
//             items.push({ productId, productName, sku: inv.sku || productData.sku || 'N/A', warehouseId: warehouse.id!, warehouseName: warehouse.name, currentStock: inv.quantity, minStock: inv.minStockLevel, maxStock: inv.maxStockLevel, deficit: Math.max(0, inv.minStockLevel - inv.quantity), status: inv.isOutOfStock ? 'out_of_stock' : 'low_stock' });
//           }
//         }
//       }
//     }

//     return {
//       items,
//       summary: { totalLowStockItems: items.filter(i => i.status === 'low_stock').length, totalOutOfStockItems: items.filter(i => i.status === 'out_of_stock').length, affectedWarehouses: new Set(items.map(i => i.warehouseId)).size, affectedProducts: new Set(items.map(i => i.productId)).size },
//     };
//   }

//   // ─── Warehouse Performance ───────────────────────────────────────────────────

//   private static async generateWarehousePerformanceReport(filters: ReportFilters): Promise<WarehousePerformanceData> {
//     const snapshot   = await getDocs(collection(db, WAREHOUSES_COLLECTION));
//     const warehouses = snapshot.docs.map(docSnap => {
//       const data          = docSnap.data();
//       const capacityUsed  = data.capacity?.used  || 0;
//       const capacityTotal = data.capacity?.total || 1;
//       return { warehouseId: docSnap.id, warehouseName: data.name || 'Unknown', code: data.code || 'N/A', address: `${data.address?.street || ''}, ${data.address?.city || ''}, ${data.address?.state || ''}`.trim(), manager: data.contact?.manager || 'N/A', totalProducts: data.stats?.productCount || 0, totalStock: data.stats?.totalStock || 0, stockValue: 0, capacityUsed, capacityTotal, capacityAvailable: data.capacity?.available || 0, utilizationRate: Math.round((capacityUsed / capacityTotal) * 100), lowStockCount: data.stats?.lowStockCount || 0, outOfStockCount: data.stats?.outOfStockCount || 0 };
//     });

//     const totalCapacity  = warehouses.reduce((s, w) => s + w.capacityTotal,    0);
//     const totalUsed      = warehouses.reduce((s, w) => s + w.capacityUsed,     0);
//     const totalAvailable = warehouses.reduce((s, w) => s + w.capacityAvailable,0);
//     const avgUtil        = warehouses.length > 0 ? warehouses.reduce((s, w) => s + w.utilizationRate, 0) / warehouses.length : 0;
//     const mostUtilized   = warehouses.reduce((mx, w) => w.utilizationRate > mx.utilizationRate ? w : mx, warehouses[0] || { warehouseName: 'N/A', utilizationRate: 0 });
//     const leastUtilized  = warehouses.reduce((mn, w) => w.utilizationRate < mn.utilizationRate ? w : mn, warehouses[0] || { warehouseName: 'N/A', utilizationRate: 0 });

//     return { warehouses, summary: { totalWarehouses: warehouses.length, totalCapacity, totalUsed, totalAvailable, averageUtilization: Math.round(avgUtil), mostUtilizedWarehouse: mostUtilized.warehouseName, leastUtilizedWarehouse: leastUtilized.warehouseName } };
//   }

//   // ─── Product Performance — delivered orders only for revenue ────────────────

//   private static async generateProductPerformanceReport(filters: ReportFilters): Promise<ProductPerformanceData> {
//     const productsRef = collection(db, PRODUCTS_COLLECTION);
//     let pC: any[] = [];
//     if (filters.categoryId)    pC.push(where('categoryId',    '==', filters.categoryId));
//     if (filters.subcategoryId) pC.push(where('subcategoryId', '==', filters.subcategoryId));
//     const productsSnapshot = await getDocs(pC.length > 0 ? query(productsRef, ...pC) : query(productsRef));

//     let oC: any[] = [];
//     if (filters.dateFrom) oC.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
//     if (filters.dateTo)   oC.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
//     const ordersSnapshot = await getDocs(oC.length > 0 ? query(collection(db, ORDERS_COLLECTION), ...oC) : query(collection(db, ORDERS_COLLECTION)));

//     const salesMap = new Map<string, { totalSold: number; revenue: number }>();
//     for (const orderDoc of ordersSnapshot.docs) {
//       if (!this.isDelivered(orderDoc.data().status)) continue;
//       for (const item of (orderDoc.data().items || [])) {
//         const pid = item.productId;
//         const ex  = salesMap.get(pid) || { totalSold: 0, revenue: 0 };
//         salesMap.set(pid, { totalSold: ex.totalSold + (item.quantity || 0), revenue: ex.revenue + (item.totalPrice || 0) });
//       }
//     }

//     const warehouses = await WarehouseService.getActiveWarehouses();
//     const products: any[] = [];

//     for (const docSnap of productsSnapshot.docs) {
//       const data      = docSnap.data();
//       const productId = docSnap.id;
//       const sales     = salesMap.get(productId) || { totalSold: 0, revenue: 0 };
//       const categoryName    = data.categoryId    ? await this.getCategoryName(data.categoryId)    : 'Uncategorized';
//       const subcategoryName = data.subcategoryId ? await this.getCategoryName(data.subcategoryId) : '';

//       let currentStock = 0;
//       for (const warehouse of warehouses) {
//         if (data.hasVariants && data.variants) {
//           for (const variant of data.variants) {
//             const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId, variant.id);
//             if (inv) currentStock += inv.quantity;
//           }
//         } else {
//           const inv = await WarehouseInventoryService.getProductInventory(warehouse.id!, productId);
//           if (inv) currentStock += inv.quantity;
//         }
//       }

//       let averagePrice = 0, totalDiscount = 0;
//       if (data.hasVariants && data.variants) {
//         const valid = data.variants.filter((v: any) => v.price > 0);
//         if (valid.length > 0) { averagePrice = valid.reduce((s: number, v: any) => s + (v.price || 0), 0) / valid.length; totalDiscount = valid.reduce((s: number, v: any) => s + (v.discount || 0), 0) / valid.length; }
//       } else { averagePrice = data.price || 0; totalDiscount = data.discount || 0; }
//       if (sales.totalSold > 0) averagePrice = sales.revenue / sales.totalSold;

//       products.push({ productId, productName: data.name || 'Unknown', sku: data.sku || 'N/A', category: categoryName, subcategory: subcategoryName || '', hasVariants: data.hasVariants || false, totalSold: sales.totalSold, revenue: sales.revenue, averagePrice, currentStock, stockTurnover: currentStock > 0 ? Math.round((sales.totalSold / currentStock) * 100) / 100 : 0, isFeatured: data.isFeatured || false, discount: totalDiscount });
//     }

//     const totalRevenue   = products.reduce((s, p) => s + p.revenue,       0);
//     const totalUnitsSold = products.reduce((s, p) => s + p.totalSold,      0);
//     const avgTurnover    = products.length > 0 ? products.reduce((s, p) => s + p.stockTurnover, 0) / products.length : 0;
//     const avgDiscount    = products.length > 0 ? products.reduce((s, p) => s + p.discount,      0) / products.length : 0;
//     const bestSelling    = products.reduce((mx, p) => p.totalSold > mx.totalSold ? p : mx, products[0] || { productName: 'N/A', totalSold: 0 });
//     const worstSelling   = products.reduce((mn, p) => (p.totalSold < mn.totalSold && p.totalSold > 0) ? p : mn, products.find(p => p.totalSold > 0) || { productName: 'N/A', totalSold: 0 });

//     return { products, summary: { totalProducts: products.length, totalRevenue, totalUnitsSold, bestSellingProduct: bestSelling.productName, worstSellingProduct: worstSelling.productName, averageStockTurnover: Math.round(avgTurnover * 100) / 100, averageDiscount: Math.round(avgDiscount * 100) / 100 } };
//   }

//   // ─── Revenue Report — delivered orders only ──────────────────────────────────

//   private static async generateRevenueReport(filters: ReportFilters): Promise<RevenueReportData> {
//     const ordersRef = collection(db, ORDERS_COLLECTION);
//     let constraints: any[] = [];
//     if (filters.dateFrom) constraints.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
//     if (filters.dateTo)   constraints.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
//     constraints.push(orderBy('orderDate', 'asc'));

//     const snapshot = await getDocs(query(ordersRef, ...constraints));
//     const dailyRevenueMap   = new Map<string, { totalOrders: number; totalRevenue: number; totalTax: number; totalShipping: number; totalDiscount: number }>();
//     const productRevenueMap = new Map<string, { productId: string; productName: string; totalSold: number; revenue: number }>();
//     const categoryRevenueMap = new Map<string, { categoryId: string; revenue: number }>();

//     for (const docSnap of snapshot.docs) {
//       const data = docSnap.data();
//       if (!this.isDelivered(data.status)) continue;
//       const orderDate = data.orderDate?.toDate();
//       if (!orderDate) continue;
//       const dateKey = orderDate.toISOString().split('T')[0];
//       const ex = dailyRevenueMap.get(dateKey) || { totalOrders: 0, totalRevenue: 0, totalTax: 0, totalShipping: 0, totalDiscount: 0 };
//       dailyRevenueMap.set(dateKey, { totalOrders: ex.totalOrders + 1, totalRevenue: ex.totalRevenue + (data.totalAmount || 0), totalTax: ex.totalTax + (data.taxAmount || 0), totalShipping: ex.totalShipping + (data.shippingCost || 0), totalDiscount: ex.totalDiscount + (data.discountAmount || 0) });
//       for (const item of (data.items || [])) {
//         const pid = item.productId, pn = item.productName || 'Unknown Product';
//         const ex2 = productRevenueMap.get(pid) || { productId: pid, productName: pn, totalSold: 0, revenue: 0 };
//         productRevenueMap.set(pid, { ...ex2, totalSold: ex2.totalSold + (item.quantity || 0), revenue: ex2.revenue + (item.totalPrice || 0) });
//       }
//     }

//     const productsSnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
//     productRevenueMap.forEach((pd, pid) => {
//       const pDoc = productsSnapshot.docs.find(d => d.id === pid);
//       if (pDoc) { const cid = pDoc.data().categoryId; if (cid) { const ex = categoryRevenueMap.get(cid) || { categoryId: cid, revenue: 0 }; categoryRevenueMap.set(cid, { categoryId: cid, revenue: ex.revenue + pd.revenue }); } }
//     });

//     const dailyRevenue: RevenueDayItem[] = Array.from(dailyRevenueMap.entries())
//       .map(([dateStr, d]) => ({ date: new Date(dateStr), totalOrders: d.totalOrders, totalRevenue: d.totalRevenue, totalTax: d.totalTax, totalShipping: d.totalShipping, totalDiscount: d.totalDiscount, netRevenue: d.totalRevenue - d.totalTax - d.totalShipping + d.totalDiscount, averageOrderValue: d.totalOrders > 0 ? d.totalRevenue / d.totalOrders : 0 }))
//       .sort((a, b) => a.date.getTime() - b.date.getTime());

//     const totalRevSum   = dailyRevenue.reduce((s, d) => s + d.totalRevenue, 0);
//     const topProducts   = Array.from(productRevenueMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10).map(p => ({ ...p, percentageOfTotal: totalRevSum > 0 ? (p.revenue / totalRevSum) * 100 : 0 }));
//     const topCategories = await Promise.all(Array.from(categoryRevenueMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 10).map(async c => ({ categoryId: c.categoryId, categoryName: await this.getCategoryName(c.categoryId), revenue: c.revenue, percentageOfTotal: totalRevSum > 0 ? (c.revenue / totalRevSum) * 100 : 0 })));

//     const totalNetRevenue = dailyRevenue.reduce((s, d) => s + d.netRevenue,    0);
//     const totalOrders     = dailyRevenue.reduce((s, d) => s + d.totalOrders,   0);
//     const totalTax        = dailyRevenue.reduce((s, d) => s + d.totalTax,      0);
//     const totalShipping   = dailyRevenue.reduce((s, d) => s + d.totalShipping, 0);
//     const totalDiscount   = dailyRevenue.reduce((s, d) => s + d.totalDiscount, 0);
//     const highestDay      = dailyRevenue.reduce((mx, d) => d.totalRevenue > mx.totalRevenue ? d : mx, dailyRevenue[0] || { totalRevenue: 0, date: new Date() });
//     const lowestDay       = dailyRevenue.reduce((mn, d) => d.totalRevenue < mn.totalRevenue ? d : mn, dailyRevenue[0] || { totalRevenue: 0, date: new Date() });

//     let growthRate = 0;
//     if (dailyRevenue.length >= 2) {
//       const half = Math.floor(dailyRevenue.length / 2);
//       const firstHalfRev  = dailyRevenue.slice(0, half).reduce((s, d) => s + d.totalRevenue, 0);
//       const secondHalfRev = dailyRevenue.slice(half).reduce((s, d) => s + d.totalRevenue, 0);
//       if (firstHalfRev > 0) growthRate = ((secondHalfRev - firstHalfRev) / firstHalfRev) * 100;
//     }

//     return { dailyRevenue, topProducts, topCategories, summary: { totalRevenue: totalRevSum, totalNetRevenue, totalOrders, totalTax, totalShipping, totalDiscount, averageOrderValue: totalOrders > 0 ? totalRevSum / totalOrders : 0, averageDailyRevenue: dailyRevenue.length > 0 ? totalRevSum / dailyRevenue.length : 0, highestRevenueDay: highestDay.date.toLocaleDateString(), lowestRevenueDay: lowestDay.date.toLocaleDateString(), growthRate: Math.round(growthRate * 100) / 100 } };
//   }

//   // ─── Returns & Cancellations Report ─────────────────────────────────────────

//   private static async generateReturnsCancellationsReport(filters: ReportFilters): Promise<ReturnsCancellationsReportData> {
//     const ordersRef = collection(db, ORDERS_COLLECTION);
//     let constraints: any[] = [];
//     if (filters.dateFrom) constraints.push(where('orderDate', '>=', Timestamp.fromDate(filters.dateFrom)));
//     if (filters.dateTo)   constraints.push(where('orderDate', '<=', Timestamp.fromDate(filters.dateTo)));
//     constraints.push(orderBy('orderDate', 'desc'));

//     const snapshot      = await getDocs(query(ordersRef, ...constraints));
//     const filteredDocs  = snapshot.docs.filter(d => this.isReturnOrCancel(d.data().status));

//     const orders: ReturnCancelOrderItem[] = filteredDocs.map(d => {
//       const data = d.data();
//       return { orderId: d.id, orderNumber: data.orderNumber || 'N/A', orderDate: data.orderDate?.toDate() || new Date(), customerName: data.customerName || 'Unknown', customerEmail: data.customerEmail || 'N/A', totalAmount: data.totalAmount || 0, status: this.formatStatus(data.status) as 'Returned' | 'Cancelled' | 'Refunded', paymentMethod: this.formatPaymentMethod(data.paymentMethod || 'N/A'), paymentStatus: this.formatStatus(data.paymentStatus || 'N/A'), items: data.items?.length || 0, reason: data.cancellationReason || data.returnReason || '' };
//     });

//     const productMap = new Map<string, { quantity: number; revenue: number }>();
//     for (const d of filteredDocs) {
//       const data = d.data();
//       if (!['returned', 'Returned', 'refunded', 'Refunded'].includes(data.status)) continue;
//       for (const item of (data.items || [])) {
//         const name = item.productName || 'Unknown Product';
//         const ex   = productMap.get(name) || { quantity: 0, revenue: 0 };
//         productMap.set(name, { quantity: ex.quantity + (item.quantity || 0), revenue: ex.revenue + (item.totalPrice || 0) });
//       }
//     }

//     const totalLostRevenue  = orders.reduce((s, o) => s + o.totalAmount, 0);
//     const totalReturned     = orders.filter(o => o.status === 'Returned').length;
//     const totalCancelled    = orders.filter(o => o.status === 'Cancelled').length;
//     const totalRefunded     = orders.filter(o => o.status === 'Refunded').length;
//     const totalAllOrders    = snapshot.size;
//     const returnRate        = totalAllOrders > 0 ? ((totalReturned + totalRefunded) / totalAllOrders) * 100 : 0;
//     const cancellationRate  = totalAllOrders > 0 ? (totalCancelled / totalAllOrders) * 100 : 0;

//     return { orders, summary: { totalReturned, totalCancelled, totalRefunded, totalOrders: orders.length, totalLostRevenue, returnRate: Math.round(returnRate * 100) / 100, cancellationRate: Math.round(cancellationRate * 100) / 100, topReturnedProducts: Array.from(productMap.entries()).map(([productName, d]) => ({ productName, ...d })).sort((a, b) => b.revenue - a.revenue).slice(0, 10) } };
//   }

//   // ─── CRUD ────────────────────────────────────────────────────────────────────

//   /** @deprecated Use getReportsPaginated */
//   static async getReports(): Promise<Report[]> {
//     try {
//       const q = query(collection(db, REPORTS_COLLECTION), orderBy('generatedAt', 'desc'));
//       const snapshot = await getDocs(q);
//       return snapshot.docs.map(doc => {
//         const data = doc.data();
//         return { id: doc.id, ...data, generatedAt: data.generatedAt?.toDate(), filters: this.deserializeFilters(data.filters || {}), data: this.deserializeReportData(data.data || {}) } as Report;
//       });
//     } catch (error) { console.error('Error fetching reports:', error); throw error; }
//   }

//   static async getReport(reportId: string): Promise<Report | null> {
//     try {
//       const snap = await getDoc(doc(db, REPORTS_COLLECTION, reportId));
//       if (!snap.exists()) return null;
//       const data = snap.data();
//       return { id: snap.id, ...data, generatedAt: data.generatedAt?.toDate(), filters: this.deserializeFilters(data.filters || {}), data: this.deserializeReportData(data.data || {}) } as Report;
//     } catch (error) { console.error('Error fetching report:', error); throw error; }
//   }

//   static async deleteReport(reportId: string): Promise<void> {
//     try { await deleteDoc(doc(db, REPORTS_COLLECTION, reportId)); }
//     catch (error) { console.error('Error deleting report:', error); throw error; }
//   }

//   static async getReportStats() {
//     try {
//       const reports = await this.getReports();
//       const byType: { [k: string]: number } = {};
//       reports.forEach(r => { byType[r.reportType] = (byType[r.reportType] || 0) + 1; });
//       return { total: reports.length, completed: reports.filter(r => r.status === 'completed').length, failed: reports.filter(r => r.status === 'failed').length, generating: reports.filter(r => r.status === 'generating').length, byType };
//     } catch (error) { console.error('Error getting report stats:', error); throw error; }
//   }
// }